import React, { useEffect, useMemo, useState } from 'react';
import {
  AlertTriangle,
  ArrowLeft,
  BookOpen,
  Building2,
  ChevronDown,
  Check,
  FileText,
  Hash,
  Home,
  Mail,
  MapPin,
  Menu,
  MessageCircle,
  Package,
  Phone,
  RefreshCcw,
  ShieldCheck,
  ShoppingCart,
  Truck,
  User,
  X
} from 'lucide-react';
import AdminWorkspace from './admin/AdminWorkspace.jsx';
import { auth, db } from './lib/firebase.js';
import {
  ADMIN_PASSWORD,
  cleanPhone,
  createCheckoutDraft,
  createManualOrder,
  dateLabel,
  getCustomerPhone,
  getOtpSessionPhone,
  isAdminLoggedIn,
  isValidIndianMobile,
  loadAdminData,
  loadBookRatings,
  loadBooks,
  loadCustomerOrders,
  loadNotifications,
  loadSiteSettings,
  loadStudentProfileWithLastOrder,
  loadStudent,
  lookupPincode,
  markNotificationsRead,
  money,
  orderFulfillmentFromItems,
  readCart,
  resendOtp,
  saveBookRating,
  saveStudentAddress,
  sendOtp,
  setCustomerPhone,
  setOtpSessionPhone,
  updateOrderStatus,
  verifyOtp,
  writeCart
} from './lib/compat.js';
import { startRazorpayPayment } from './lib/razorpay.js';
import { SITE_PAGE_DEFINITIONS, sitePageContent } from './lib/sitePages.js';

const STATUS_LABELS = {
  processing: 'Payment Received',
  paid: 'Payment Received',
  shipped: 'Shipped',
  delivered: 'Delivered',
  cancelled: 'Cancelled'
};

const STATUS_OPTIONS = ['processing', 'shipped', 'delivered', 'cancelled'];
const SITE_ORIGIN = 'https://books.muppadaitrainingacademy.com';
const PROFESSIONAL_COURIER_OFFICIAL_TRACKING_URL = 'https://www.tpcindia.com/';
const CLIENT_BUILD_ID = '20260612-cache-refresh-1';
const POLICY_PAGES = Object.fromEntries(SITE_PAGE_DEFINITIONS.map((page) => [page.slug, page]));

const CATEGORY_PAGES = [
  {
    slug: 'tnusrb-si-books',
    label: 'TNUSRB SI Books',
    shortLabel: 'SI Books',
    cat: 'TNUSRB',
    terms: ['tnusrb', 'si'],
    title: 'TNUSRB SI Books 2026 | Tamil & English Study Materials',
    description: 'Buy TNUSRB SI books for Tamil and English medium students. Choose complete combo books, separate subject books and practice sets with courier delivery across Tamil Nadu.',
    intro: 'Complete TNUSRB Sub Inspector preparation books for students who want syllabus-based study material, practice questions and reliable courier delivery.',
    faqs: [
      ['Which TNUSRB SI books are available?', 'You can buy TNUSRB SI combo books, Tamil medium separate books, English medium separate books and practice sets.'],
      ['Are these books suitable for Tamil medium students?', 'Yes. Tamil medium books and practice materials are available for TNUSRB SI preparation.'],
      ['How fast will I receive the books?', 'Most orders are dispatched by courier and usually reach students in 2 to 4 working days after shipment.']
    ]
  },
  {
    slug: 'tnusrb-si-tamil-books',
    label: 'TNUSRB SI Tamil Books',
    shortLabel: 'SI Tamil',
    cat: 'TNUSRB',
    terms: ['tnusrb', 'si', 'tamil'],
    title: 'TNUSRB SI Tamil Books 2026 | Tamil Medium Study Materials',
    description: 'Buy TNUSRB SI Tamil medium books for 2026 exam preparation. Includes General Tamil, Science, Social Science and complete study materials from Muppadai.',
    intro: 'Tamil medium TNUSRB SI books for students preparing with clear subject-wise material, sample previews and verified student reviews.',
    faqs: [
      ['Which page should I use for TNUSRB SI Tamil books?', 'This page groups the Tamil medium TNUSRB SI books and related study materials in one place.'],
      ['Can I buy separate subject books?', 'Yes. Select the required variants and quantity from each product page before checkout.'],
      ['Do these books include General Tamil?', 'Available variants may include General Tamil and other TNUSRB SI Tamil medium subjects.']
    ]
  },
  {
    slug: 'tnusrb-si-english-books',
    label: 'TNUSRB SI English Books',
    shortLabel: 'SI English',
    cat: 'TNUSRB',
    terms: ['tnusrb', 'si', 'english'],
    title: 'TNUSRB SI English Books 2026 | English Medium Study Materials',
    description: 'Buy TNUSRB SI English medium books and practice materials for Sub Inspector exam preparation with courier delivery from Muppadai.',
    intro: 'English medium TNUSRB SI books for students who prefer subject-wise preparation material and exam-focused practice.',
    faqs: [
      ['Are English medium TNUSRB SI books available?', 'Yes. English medium separate books and combo options are listed here when available.'],
      ['Can I order multiple books together?', 'Yes. Add required books to cart and complete checkout once.']
    ]
  },
  {
    slug: 'tnusrb-si-practice-set',
    label: 'TNUSRB SI Practice Set',
    shortLabel: 'SI Practice',
    cat: 'TNUSRB',
    terms: ['tnusrb', 'si', 'practice'],
    title: 'TNUSRB SI Practice Set Books | Model Questions & Tests',
    description: 'Buy TNUSRB SI practice set books with model questions and exam-focused preparation material for Tamil Nadu Sub Inspector aspirants.',
    intro: 'Practice set books for students who want repeated revision, model questions and exam-style preparation before the TNUSRB SI exam.',
    faqs: [
      ['What is included in practice set books?', 'Practice sets usually include model questions and exam-oriented exercises for revision.'],
      ['Can I buy practice sets with main books?', 'Yes. You can add both study material and practice sets to the same cart.']
    ]
  },
  {
    slug: 'tnusrb-pc-books',
    label: 'TNUSRB PC Books',
    shortLabel: 'PC Books',
    cat: 'TNUSRB',
    terms: ['tnusrb', 'pc'],
    title: 'TNUSRB PC Books 2026 | Police Constable Study Materials',
    description: 'Buy TNUSRB PC books for Police Constable exam preparation. Choose combo books, separate books and practice sets from Muppadai.',
    intro: 'TNUSRB Police Constable books for students preparing for PC, Jail Warder and Fireman related exams.',
    faqs: [
      ['Are TNUSRB PC combo books available?', 'Yes. Combo and separate book options are listed here when active in the store.']
    ]
  },
  {
    slug: 'scert-tamil-books',
    label: 'SCERT Tamil Books',
    shortLabel: 'SCERT',
    cat: 'SCERT',
    terms: ['scert'],
    title: 'SCERT Tamil Books | Government Exam Study Materials',
    description: 'Buy SCERT Tamil books and combo study materials for government exam preparation with sample previews and courier delivery.',
    intro: 'SCERT Tamil study materials for students who want subject-wise preparation for government exams.',
    faqs: [
      ['Are SCERT books available as separate books?', 'Yes. Separate and combo options are shown here based on current stock.']
    ]
  },
  {
    slug: 'ssc-gd-books',
    label: 'SSC GD Books',
    shortLabel: 'SSC GD',
    cat: 'CENTRAL',
    terms: ['ssc', 'gd'],
    title: 'SSC GD Books 2026 | Tamil & English Study Materials',
    description: 'Buy SSC GD books, combo sets and practice books for central government exam preparation from Muppadai.',
    intro: 'SSC GD books for students preparing for central government uniform service exams.',
    faqs: [
      ['Are SSC GD Tamil books available?', 'Tamil and English options are listed here when available in stock.']
    ]
  },
  {
    slug: 'rrb-group-d-books',
    label: 'RRB Group D Books',
    shortLabel: 'RRB',
    cat: 'CENTRAL',
    terms: ['rrb', 'group', 'd'],
    title: 'RRB Group D Books | Railway Exam Study Materials',
    description: 'Buy RRB Group D books and study materials for railway exam preparation with delivery across Tamil Nadu.',
    intro: 'Railway Group D preparation books for students who want focused study material and practice support.',
    faqs: [
      ['Can I buy RRB Group D Tamil books?', 'Tamil and English book options are shown here when they are active in the catalog.']
    ]
  },
  {
    slug: 'indian-army-books',
    label: 'Indian Army Books',
    shortLabel: 'Army',
    cat: 'CENTRAL',
    terms: ['indian', 'army'],
    title: 'Indian Army Books | Army Exam Study Materials',
    description: 'Buy Indian Army exam books and study materials for GD, Tradesman and related defence exam preparation.',
    intro: 'Indian Army preparation books for students preparing for defence and uniform service exams.',
    faqs: [
      ['Are Army books delivered by courier?', 'Yes. Physical books are delivered by courier after successful payment.']
    ]
  },
  {
    slug: 'ebooks',
    label: 'E-Books',
    shortLabel: 'E-Books',
    type: 'ebook',
    title: 'Muppadai E-Books | Instant Digital Exam Study Materials',
    description: 'Buy Muppadai e-books for TNUSRB, SSC, RRB and government exam preparation. View sample PDFs and choose instant digital study materials.',
    intro: 'Digital study materials for students who want instant access, sample previews and mobile-friendly exam preparation.',
    faqs: [
      ['How do Muppadai e-books work?', 'E-book details and access instructions are shown on the product page. After payment, support will share access based on the product instructions.'],
      ['Are sample PDFs available?', 'When available, sample PDFs are shown in the Sample Book Preview section before purchase.']
    ]
  }
];

const CATEGORY_PAGE_MAP = Object.fromEntries(CATEGORY_PAGES.map((page) => [page.slug, page]));

function slugify(value) {
  return String(value || '')
    .toLowerCase()
    .replace(/&/g, ' and ')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 90);
}

function bookSlug(book) {
  return slugify(book.seoSlug || book.title || book.id || 'book');
}

function findBookBySlug(books, slug) {
  return books.find((book) => (
    bookSlug(book) === slug ||
    slugify(book.title || '') === slug ||
    book.id === slug ||
    slug?.endsWith(`-${book.id}`)
  ));
}

function bookSearchText(book) {
  return [
    book.title,
    book.cat,
    book.productType,
    book.ebook?.enabled ? 'ebook e-book digital pdf' : '',
    ...(book.variants || []).map((variant) => variant.name || variant.vn || '')
  ].join(' ').toLowerCase();
}

function bookProductType(book) {
  const type = String(book.productType || '').toLowerCase();
  if (type === 'ebook' || type === 'combo') return type;
  if (book.ebook?.enabled) return 'ebook';
  return 'physical';
}

function itemProductType(item, books = []) {
  const explicit = String(item?.productType || '').toLowerCase();
  if (explicit === 'ebook' || explicit === 'combo' || explicit === 'physical') return explicit;
  const book = books.find((entry) => entry.id === item?.bid);
  return bookProductType(book || {});
}

function itemHasEbook(item, books = []) {
  const type = itemProductType(item, books);
  const book = books.find((entry) => entry.id === item?.bid);
  return type === 'ebook' || type === 'combo' || book?.ebook?.enabled === true;
}

function itemHasPhysical(item, books = []) {
  const type = itemProductType(item, books);
  return type === 'physical' || type === 'combo';
}

function orderHasEbooks(order, books = []) {
  return (order.items || []).some((item) => itemHasEbook(item, books)) || order.hasEbook === true || order.fulfillmentType === 'ebook' || order.fulfillmentType === 'mixed';
}

function orderHasPhysical(order, books = []) {
  return (order.items || []).some((item) => itemHasPhysical(item, books)) || order.hasPhysical === true || order.fulfillmentType === 'physical' || order.fulfillmentType === 'mixed' || !orderHasEbooks(order, books);
}

function orderFulfillmentType(order, books = []) {
  const saved = String(order?.fulfillmentType || '').toLowerCase();
  if (saved === 'ebook' || saved === 'mixed' || saved === 'physical') return saved;
  const hasEbook = orderHasEbooks(order, books);
  const hasPhysical = (order.items || []).some((item) => itemHasPhysical(item, books));
  if (hasEbook && hasPhysical) return 'mixed';
  if (hasEbook) return 'ebook';
  return orderFulfillmentFromItems(order.items || []);
}

function categoryMatchesBook(page, book) {
  if (!book) return false;
  if (page.type === 'ebook') return bookProductType(book) === 'ebook' || bookProductType(book) === 'combo' || book.ebook?.enabled === true;
  const text = bookSearchText(book);
  return (page.terms || []).every((term) => text.includes(term));
}

function primaryCategoryForBook(book) {
  return CATEGORY_PAGES.find((page) => page.type !== 'ebook' && categoryMatchesBook(page, book)) || CATEGORY_PAGES.find((page) => page.cat && page.cat === book.cat) || null;
}

function categoryUrl(slug) {
  return `${SITE_ORIGIN}/${slug}`;
}

function pathRoute() {
  const path = window.location.pathname.replace(/\/+$/, '') || '/';
  if (window.location.hash === '#admin') {
    window.history.replaceState({}, '', '/adminlogin');
    return { name: 'admin' };
  }
  if (path === '/adminlogin') return { name: 'admin' };
  if (path === '/orders') return { name: 'orders' };
  if (path === '/profile') return { name: 'profile' };
  if (path === '/thank-you') return { name: 'thankyou' };
  if (path === '/track') return { name: 'track' };
  if (POLICY_PAGES[path.slice(1)]) return { name: 'policy', slug: path.slice(1) };
  if (path.startsWith('/book/')) return { name: 'book', slug: decodeURIComponent(path.slice(6)) };
  const cleanPath = path.slice(1);
  if (CATEGORY_PAGE_MAP[cleanPath]) return { name: 'category', slug: cleanPath };
  return { name: 'store' };
}

function setMeta(name, content, attr = 'name') {
  if (!content) return;
  let tag = document.head.querySelector(`meta[${attr}="${name}"]`);
  if (!tag) {
    tag = document.createElement('meta');
    tag.setAttribute(attr, name);
    document.head.appendChild(tag);
  }
  tag.setAttribute('content', content);
}

function setCanonical(url) {
  let link = document.head.querySelector('link[rel="canonical"]');
  if (!link) {
    link = document.createElement('link');
    link.rel = 'canonical';
    document.head.appendChild(link);
  }
  link.href = url;
}

function upsertJsonLd(id, data) {
  let script = document.getElementById(id);
  if (!script) {
    script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = id;
    document.head.appendChild(script);
  }
  script.textContent = JSON.stringify(data);
}

function removeJsonLd(id) {
  document.getElementById(id)?.remove();
}

function stripHtml(value) {
  const div = document.createElement('div');
  div.innerHTML = value || '';
  return (div.textContent || div.innerText || '').replace(/\s+/g, ' ').trim();
}

function descriptionPoints(value) {
  const div = document.createElement('div');
  div.innerHTML = value || '';
  const listItems = Array.from(div.querySelectorAll('li')).map((item) => stripHtml(item.innerHTML)).filter(Boolean);
  if (listItems.length) return listItems;
  return stripHtml(value)
    .split(/(?:\.\s+|।\s+|\n+)/)
    .map((item) => item.trim())
    .filter((item) => item.length > 8)
    .slice(0, 10);
}

function bookDetailPoints(book) {
  const custom = Array.isArray(book.detailPoints)
    ? book.detailPoints
    : String(book.detailPoints || '').split('\n');
  const clean = custom.map((item) => String(item || '').trim()).filter(Boolean);
  return clean.length ? clean : descriptionPoints(book.description);
}

function bookSampleImages(book) {
  const images = Array.isArray(book.sampleImages)
    ? book.sampleImages
    : String(book.sampleImages || '').split('\n');
  return images.map((url) => String(url || '').trim()).filter(Boolean).slice(0, 8);
}

function bookSampleResources(book) {
  const imageResources = bookSampleImages(book).map((url, index) => ({ type: 'image', url, label: `Preview page ${index + 1}` }));
  const pdfResources = (book.variants || [])
    .map((variant) => ({
      url: String(variant.samplePdfUrl || '').trim(),
      label: String(variant.name || variant.vn || '').trim()
    }))
    .filter((item) => item.url)
    .map((item) => ({ type: 'pdf', url: item.url, label: item.label || sampleFileName(item.url) }));
  return [...imageResources, ...pdfResources].slice(0, 8);
}

function sampleFileName(url) {
  try {
    const parsed = new URL(url);
    const last = decodeURIComponent(parsed.pathname.split('/').filter(Boolean).pop() || parsed.hostname);
    return last
      .replace(/\.(pdf|docx?|png|jpe?g|webp)$/i, '')
      .replace(/[-_]+/g, ' ')
      .replace(/\s+/g, ' ')
      .trim() || 'Sample PDF';
  } catch {
    return String(url || '')
      .split('/')
      .filter(Boolean)
      .pop()
      ?.replace(/\.(pdf|docx?)$/i, '')
      .replace(/[-_]+/g, ' ')
      .trim() || 'Sample PDF';
  }
}

function bookFaqs(book) {
  if (Array.isArray(book.faqs)) {
    return book.faqs
      .map((item) => ({ q: item.q || item.question || '', a: item.a || item.answer || '' }))
      .filter((item) => item.q && item.a)
      .slice(0, 8);
  }
  return String(book.faqs || '')
    .split('\n')
    .map((line) => {
      const [q, ...rest] = line.split('|');
      return { q: String(q || '').trim(), a: rest.join('|').trim() };
    })
    .filter((item) => item.q && item.a)
    .slice(0, 8);
}

function firstVariant(book) {
  return Array.isArray(book.variants) && book.variants.length ? book.variants[0] : null;
}

function bookPrice(book) {
  const prices = (book.variants || []).map((variant) => Number(variant.price || 0)).filter(Boolean);
  return prices.length ? Math.min(...prices) : 0;
}

function orderStatus(order) {
  return order.deliveryStatus || order.status || 'processing';
}

function orderStudent(order) {
  return order.delivery?.name || order.studentName || 'Customer';
}

function orderPhone(order) {
  return cleanPhone(order.delivery?.phone || order.studentPhone || order.phone || '');
}

function totalUnits(order) {
  return (order.items || []).reduce((sum, item) => sum + Number(item.qty || 1), 0);
}

function orderTotal(order) {
  return (order.items || []).reduce((sum, item) => sum + Number(item.price || 0) * Number(item.qty || 1), 0) || Number(order.total || 0);
}

function orderStatusDate(order, status) {
  const value =
    status === 'shipped'
      ? order.shippedAt || order.dispatchedAt || order.updatedAt
      : status === 'delivered'
        ? order.deliveredAt || order.updatedAt
        : status === 'paid' || status === 'processing'
          ? order.paidAt || order.createdAt
          : order.updatedAt || order.createdAt;
  return value ? dateLabel(value) : '';
}

function orderStudentStatusCopy(order, fulfillmentType = 'physical') {
  if (fulfillmentType === 'ebook') {
    return {
      title: 'E-book access ready',
      date: orderStatusDate(order, 'processing'),
      body: 'Your watermarked PDF is ready in My E-Books. Download it using the same verified mobile number used for purchase.',
      tone: 'ebook'
    };
  }
  const status = orderStatus(order);
  if (status === 'shipped') {
    const tracking = String(order.trackingNumber || '').trim();
    return {
      title: 'Dispatched via courier',
      date: orderStatusDate(order, 'shipped'),
      body: tracking
        ? `Your book is dispatched via Professional Couriers. Tracking number: ${tracking}. You will receive it in 2-4 working days.`
        : 'Your book is dispatched via courier today. You will receive it in 2-4 working days.',
      tone: 'shipped'
    };
  }
  if (status === 'delivered') {
    return {
      title: 'Delivered',
      date: orderStatusDate(order, 'delivered'),
      body: 'Thanks for confirming. Your order is marked as delivered.',
      tone: 'delivered'
    };
  }
  if (status === 'cancelled') {
    return {
      title: 'Order cancelled',
      date: orderStatusDate(order, status),
      body: 'This order has been cancelled.',
      tone: 'cancelled'
    };
  }
  return {
    title: 'Payment received',
    date: orderStatusDate(order, 'processing'),
    body: fulfillmentType === 'mixed'
      ? 'We received your payment. Your e-book access is ready, and your physical books will be packed and dispatched soon.'
      : 'We received your payment. Your books will be packed and dispatched soon.',
    tone: 'processing'
  };
}

function isoDate(value) {
  if (!value) return '';
  const date = value?.toDate ? value.toDate() : new Date(value);
  return Number.isNaN(date.getTime()) ? '' : date.toISOString().slice(0, 10);
}

function inPeriod(value, period, from, to) {
  if (!period && !from && !to) return true;
  const date = value?.toDate ? value.toDate() : new Date(value || '');
  if (Number.isNaN(date.getTime())) return false;
  const start = new Date();
  start.setHours(0, 0, 0, 0);
  if (period && period !== 'custom') {
    const days = Number(period);
    const min = new Date(start);
    min.setDate(min.getDate() - Math.max(days - 1, 0));
    return date >= min;
  }
  if (from) {
    const min = new Date(from);
    min.setHours(0, 0, 0, 0);
    if (date < min) return false;
  }
  if (to) {
    const max = new Date(to);
    max.setHours(23, 59, 59, 999);
    if (date > max) return false;
  }
  return true;
}

function downloadCsv(filename, rows) {
  const keys = Object.keys(rows[0] || {});
  const csv = [
    keys.join(','),
    ...rows.map((row) => keys.map((key) => `"${String(row[key] ?? '').replace(/"/g, '""')}"`).join(','))
  ].join('\n');
  const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}

function App() {
  const [route, setRoute] = useState(() => pathRoute());
  const [books, setBooks] = useState([]);
  const [ratings, setRatings] = useState({});
  const [cart, setCart] = useState(() => readCart());
  const [cartOpen, setCartOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState(null);
  const [siteSettings, setSiteSettings] = useState({});
  const [successOrder, setSuccessOrder] = useState(null);

  useEffect(() => {
    window.__mtaClientBuild = CLIENT_BUILD_ID;
    const onPop = () => setRoute(pathRoute());
    window.addEventListener('popstate', onPop);
    if (window.location.hash) setRoute(pathRoute());
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    loadBooks()
      .then((items) => {
        if (mounted) setBooks(items);
      })
      .catch((err) => {
        if (mounted) setError(err.message || 'Unable to load books.');
      })
      .finally(() => mounted && setLoading(false));
    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    loadSiteSettings()
      .then((settings) => {
        if (!settings) return;
        setSiteSettings(settings);
        // GA4
        const gaId = settings.gaId;
        if (gaId && window.__mtaGaId !== gaId) {
          window.__mtaGaId = gaId;
          const script = document.createElement('script');
          script.async = true;
          script.src = `https://www.googletagmanager.com/gtag/js?id=${gaId}`;
          document.head.appendChild(script);
          window.dataLayer = window.dataLayer || [];
          window.gtag = function gtag() { window.dataLayer.push(arguments); };
          window.gtag('js', new Date());
          window.gtag('config', gaId);
        }
        // FB Pixel
        const fbId = settings.fbPixelId;
        if (fbId && window.__mtaFbId !== fbId) {
          window.__mtaFbId = fbId;
          /* eslint-disable */
          !function(f,b,e,v,n,t,s){if(f.fbq)return;n=f.fbq=function(){n.callMethod?n.callMethod.apply(n,arguments):n.queue.push(arguments)};if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';n.queue=[];t=b.createElement(e);t.async=!0;t.src=v;s=b.getElementsByTagName(e)[0];s.parentNode.insertBefore(t,s)}(window,document,'script','https://connect.facebook.net/en_US/fbevents.js');
          /* eslint-enable */
          if (window.fbq) { window.fbq('init', fbId); window.fbq('track', 'PageView'); }
        }
        // Favicon
        if (settings.faviconUrl) {
          let link = document.querySelector("link[rel~='icon']");
          if (!link) { link = document.createElement('link'); link.rel = 'icon'; document.head.appendChild(link); }
          link.href = settings.faviconUrl;
        }
        // Title
        if (settings.siteName) document.title = settings.siteName;
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    writeCart(cart);
  }, [cart]);

  useEffect(() => {
    loadBookRatings().then(setRatings).catch(() => setRatings({}));
  }, []);

  function go(next) {
    const path = next === 'store' ? '/' : next === 'admin' ? '/adminlogin' : `/${next}`;
    window.history.pushState({}, '', path);
    setRoute(pathRoute());
  }

  function goBooks() {
    window.history.pushState({}, '', '/');
    setRoute({ name: 'store' });
    setTimeout(() => document.getElementById('catalog')?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 80);
  }

  function goCategory(slug) {
    window.history.pushState({}, '', `/${slug}`);
    setRoute({ name: 'category', slug });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  function openBook(book) {
    window.history.pushState({}, '', `/book/${bookSlug(book)}`);
    setRoute({ name: 'book', slug: bookSlug(book) });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  function addToCart(book, variant, qty = 1) {
    const picked = variant || firstVariant(book);
    if (!picked) return;
    const productType = bookProductType(book);
    const isEbookOnly = productType === 'ebook';
    const addQty = isEbookOnly ? 1 : Math.max(1, Number(qty || 1));
    setCart((current) => {
      const key = `${book.id}:${picked.name}`;
      const existing = current.find((item) => item.key === key);
      if (existing) {
        return current.map((item) => (item.key === key ? { ...item, qty: isEbookOnly ? 1 : Number(item.qty || 1) + addQty } : item));
      }
      return [
        ...current,
        {
          key,
          bid: book.id,
          bt: book.title || 'Book',
          bookTitle: book.title || 'Book',
          vn: picked.name || 'Default',
          variantName: picked.name || 'Default',
          price: Number(picked.price || 0),
          qty: addQty,
          img: picked.imageUrl || book.imageUrl || '',
          productType,
          ebookFileName: book.ebook?.fileName || '',
          ebookAccessNote: book.ebook?.accessNote || book.ebook?.deliveryMode || ''
        }
      ];
    });
    setCartOpen(true);
  }

  function updateCartQty(key, delta) {
    setCart((current) =>
      current
        .map((item) => {
          if (item.key !== key) return item;
          if (String(item.productType || '').toLowerCase() === 'ebook') return { ...item, qty: 1 };
          return { ...item, qty: Math.max(1, Number(item.qty || 1) + delta) };
        })
        .filter((item) => item.qty > 0)
    );
  }

  function removeCartItem(key) {
    setCart((current) => current.filter((item) => item.key !== key));
  }

  function handleCheckoutSuccess(order) {
    setSuccessOrder(order);
    setCart([]);
    setCartOpen(false);
    window.history.pushState({}, '', '/thank-you');
    setRoute({ name: 'thankyou' });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  const cartCount = cart.reduce((sum, item) => {
    const isEbookItem = String(item.productType || '').toLowerCase() === 'ebook';
    return sum + (isEbookItem ? 1 : Number(item.qty || 1));
  }, 0);
  const phone = getOtpSessionPhone() || getCustomerPhone();
  const [chatOpen, setChatOpen] = useState(false);
  const routeBook = route.name === 'book' ? findBookBySlug(books, route.slug) : null;
  const routeCategory = route.name === 'category' ? CATEGORY_PAGE_MAP[route.slug] : null;
  const routePolicy = route.name === 'policy' ? sitePageContent(siteSettings, route.slug) : null;

  useEffect(() => {
    const siteName = siteSettings.siteName || 'Muppadai Training Academy Books';
    const homeTitle = 'Muppadai Books - TNUSRB, TNPSC, SSC, RRB Exam Books';
    const homeDesc = 'Buy Muppadai Training Academy government exam books for TNUSRB, TNPSC, SSC GD, RRB and Indian Army preparation with courier delivery across Tamil Nadu.';
    if (route.name === 'book' && routeBook) {
      const url = `${SITE_ORIGIN}/book/${bookSlug(routeBook)}`;
      const description = routeBook.metaDescription || stripHtml(routeBook.description) || `${routeBook.title} exam preparation book from Muppadai Training Academy. Order online with delivery across Tamil Nadu.`;
      const title = routeBook.seoTitle || `${routeBook.title} | ${siteName}`;
      const image = routeBook.imageUrl || `${SITE_ORIGIN}/og-image.jpg`;
      const rating = ratings[routeBook.id] || {};
      const categoryPage = primaryCategoryForBook(routeBook);
      const faqs = bookFaqs(routeBook);
      document.title = title;
      setMeta('description', description.slice(0, 160));
      if (routeBook.focusKeywords) setMeta('keywords', String(routeBook.focusKeywords).slice(0, 240));
      setMeta('robots', 'index, follow, max-image-preview:large');
      setMeta('og:site_name', siteName, 'property');
      setMeta('og:title', title, 'property');
      setMeta('og:description', description.slice(0, 200), 'property');
      setMeta('og:type', 'product', 'property');
      setMeta('og:url', url, 'property');
      setMeta('og:image', image, 'property');
      const price = bookPrice(routeBook);
      if (price) setMeta('product:price:amount', String(price), 'property');
      setMeta('product:price:currency', 'INR', 'property');
      setMeta('twitter:card', 'summary_large_image');
      setMeta('twitter:title', title);
      setMeta('twitter:description', description.slice(0, 200));
      setMeta('twitter:image', image);
      setCanonical(url);
      const offers = (routeBook.variants || []).filter((variant) => Number(variant.price || 0) > 0).map((variant) => ({
        '@type': 'Offer',
        name: variant.name || 'Book',
        priceCurrency: 'INR',
        price: String(Number(variant.price || 0)),
        availability: routeBook.inStock === false || variant.inStock === false ? 'https://schema.org/OutOfStock' : 'https://schema.org/InStock',
        url
      }));
      const productSchema = {
        '@context': 'https://schema.org',
        '@type': 'Product',
        name: routeBook.title,
        description,
        image: image ? [image] : undefined,
        brand: { '@type': 'Brand', name: 'Muppadai Training Academy' },
        category: routeBook.cat || 'Exam Books',
        sku: routeBook.id,
        additionalProperty: [
          { '@type': 'PropertyValue', name: 'Product type', value: bookProductType(routeBook) === 'ebook' ? 'E-book' : bookProductType(routeBook) === 'combo' ? 'Physical book + E-book' : 'Physical book' },
          routeBook.ebook?.format ? { '@type': 'PropertyValue', name: 'E-book format', value: routeBook.ebook.format } : null,
          routeBook.ebook?.pages ? { '@type': 'PropertyValue', name: 'Pages', value: String(routeBook.ebook.pages) } : null
        ].filter(Boolean),
        offers: offers.length === 1 ? offers[0] : { '@type': 'AggregateOffer', priceCurrency: 'INR', lowPrice: String(bookPrice(routeBook)), offerCount: String(offers.length || 1), offers },
        aggregateRating: rating.count ? { '@type': 'AggregateRating', ratingValue: Number(rating.average || 0).toFixed(1), reviewCount: String(rating.count), bestRating: '5', worstRating: '1' } : undefined,
        review: (rating.reviews || []).filter((item) => item.review).slice(0, 5).map((item) => ({
          '@type': 'Review',
          reviewRating: { '@type': 'Rating', ratingValue: String(item.rating), bestRating: '5' },
          author: { '@type': 'Person', name: 'Verified Student' },
          reviewBody: item.review
        }))
      };
      upsertJsonLd('mta-product-schema', productSchema);
      if (faqs.length) {
        upsertJsonLd('mta-faq-schema', {
          '@context': 'https://schema.org',
          '@type': 'FAQPage',
          mainEntity: faqs.map((item) => ({
            '@type': 'Question',
            name: item.q,
            acceptedAnswer: { '@type': 'Answer', text: item.a }
          }))
        });
      } else {
        removeJsonLd('mta-faq-schema');
      }
      upsertJsonLd('mta-breadcrumb-schema', {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_ORIGIN },
          { '@type': 'ListItem', position: 2, name: categoryPage?.label || routeBook.cat || 'Books', item: categoryPage ? categoryUrl(categoryPage.slug) : `${SITE_ORIGIN}/#catalog` },
          { '@type': 'ListItem', position: 3, name: routeBook.title, item: url }
        ]
      });
      removeJsonLd('mta-ssr-product-schema');
    } else if (route.name === 'category' && routeCategory) {
      const url = categoryUrl(routeCategory.slug);
      const categoryBooks = books.filter((book) => categoryMatchesBook(routeCategory, book)).slice(0, 24);
      document.title = routeCategory.title;
      setMeta('description', routeCategory.description);
      setMeta('robots', 'index, follow, max-image-preview:large');
      setMeta('og:site_name', siteName, 'property');
      setMeta('og:title', routeCategory.title, 'property');
      setMeta('og:description', routeCategory.description, 'property');
      setMeta('og:type', 'website', 'property');
      setMeta('og:url', url, 'property');
      setMeta('twitter:card', 'summary');
      setMeta('twitter:title', routeCategory.title);
      setMeta('twitter:description', routeCategory.description);
      setCanonical(url);
      upsertJsonLd('mta-product-schema', {
        '@context': 'https://schema.org',
        '@type': 'ItemList',
        name: routeCategory.label,
        description: routeCategory.description,
        url,
        itemListElement: categoryBooks.map((book, index) => ({
          '@type': 'ListItem',
          position: index + 1,
          url: `${SITE_ORIGIN}/book/${bookSlug(book)}`,
          name: book.title
        }))
      });
      upsertJsonLd('mta-breadcrumb-schema', {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_ORIGIN },
          { '@type': 'ListItem', position: 2, name: routeCategory.label, item: url }
        ]
      });
      upsertJsonLd('mta-faq-schema', {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: (routeCategory.faqs || []).map(([q, a]) => ({
          '@type': 'Question',
          name: q,
          acceptedAnswer: { '@type': 'Answer', text: a }
        }))
      });
      removeJsonLd('mta-ssr-product-schema');
    } else {
      const noindexRoute = ['admin', 'orders', 'profile', 'thankyou', 'track'].includes(route.name);
      const pageUrl = routePolicy ? `${SITE_ORIGIN}/${route.slug}` : '';
      const pageTitle = routePolicy ? routePolicy.heading : homeTitle;
      const pageDescription = routePolicy ? routePolicy.sections.map((section) => section.body).join(' ').slice(0, 160) : homeDesc;
      document.title = routePolicy ? `${pageTitle} | ${siteName}` : route.name === 'admin' ? 'Admin Login | Muppadai Books' : homeTitle;
      setMeta('description', pageDescription);
      setMeta('robots', noindexRoute ? 'noindex, nofollow' : 'index, follow, max-image-preview:large');
      setMeta('og:site_name', siteName, 'property');
      setMeta('og:title', pageTitle, 'property');
      setMeta('og:description', pageDescription, 'property');
      setMeta('og:type', 'website', 'property');
      setMeta('og:url', pageUrl || `${SITE_ORIGIN}${route.name === 'orders' ? '/orders' : route.name === 'profile' ? '/profile' : '/'}`, 'property');
      setMeta('twitter:card', 'summary');
      setMeta('twitter:title', pageTitle);
      setMeta('twitter:description', pageDescription);
      setCanonical(pageUrl || `${SITE_ORIGIN}${route.name === 'orders' ? '/orders' : route.name === 'profile' ? '/profile' : '/'}`);
      removeJsonLd('mta-breadcrumb-schema');
      removeJsonLd('mta-ssr-product-schema');
      removeJsonLd('mta-faq-schema');
      upsertJsonLd('mta-product-schema', {
        '@context': 'https://schema.org',
        '@type': 'WebSite',
        name: siteName,
        url: SITE_ORIGIN
      });
    }
  }, [route, routeBook, routeCategory, books, ratings, siteSettings]);

  if (route.name === 'admin') {
    return <AdminWorkspace onStore={() => go('store')} />;
  }

  return (
    <div className="storeShell">
      <StoreHeader cartCount={cartCount} onCart={() => setCartOpen(true)} onOrders={() => go('orders')} onProfile={() => go('profile')} onStore={() => go('store')} onBooks={goBooks} onCategory={goCategory} onTrack={() => go('track')} logoUrl={siteSettings.logoUrl} siteName={siteSettings.siteName} />

      {error && (
        <div className="storeNotice">
          <AlertTriangle size={18} />
          <span>{error}</span>
        </div>
      )}

      {route.name === 'orders' ? (
        <CustomerOrders books={books} onRated={() => loadBookRatings().then(setRatings).catch(() => {})} />
      ) : route.name === 'profile' ? (
        <ProfilePage books={books} onRated={() => loadBookRatings().then(setRatings).catch(() => {})} />
      ) : route.name === 'thankyou' ? (
        <ThankYouPage order={successOrder} onStore={() => go('store')} onOrders={() => go('orders')} />
      ) : route.name === 'track' ? (
        <TrackingPage />
      ) : route.name === 'policy' && routePolicy ? (
        <PolicyPage page={routePolicy} slug={route.slug} />
      ) : route.name === 'book' ? (
        <BookDetailPage book={routeBook} books={books} loading={loading} ratings={ratings} onAdd={addToCart} onBack={() => go('store')} onProfile={() => go('profile')} onOpenBook={openBook} />
      ) : route.name === 'category' && routeCategory ? (
        <CategoryLandingPage page={routeCategory} books={books} loading={loading} ratings={ratings} onOpenBook={openBook} onCategory={goCategory} />
      ) : (
        <Storefront books={books} loading={loading} ratings={ratings} onOpenBook={openBook} onCategory={goCategory} />
      )}

      <SiteFooter onNavigate={go} settings={siteSettings} />

      <CartDrawer
        open={cartOpen}
        cart={cart}
        onClose={() => setCartOpen(false)}
        onAddMore={() => {
          setCartOpen(false);
          goBooks();
        }}
        onQty={updateCartQty}
        onRemove={removeCartItem}
        onClear={() => setCart([])}
        onCheckoutSuccess={handleCheckoutSuccess}
      />

      {/* Live chat bubble */}
      {!cartOpen && <ChatBubble phone={phone} open={chatOpen} onOpen={() => setChatOpen(true)} onClose={() => setChatOpen(false)} onLogin={() => go('profile')} />}

      {!cartOpen && (
        <StoreBottomNav
          cartCount={cartCount}
          onCart={() => setCartOpen(true)}
          onBooks={goBooks}
          onEbooks={() => goCategory('ebooks')}
          onOrders={() => go('orders')}
          onProfile={() => go('profile')}
          onStore={() => go('store')}
        />
      )}
    </div>
  );
}

function StoreHeader({ cartCount, onCart, onOrders, onProfile, onStore, onBooks, onCategory, onTrack, logoUrl, siteName }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const name = siteName || 'Muppadai Training Academy';
  const closeAndRun = (fn) => {
    setMenuOpen(false);
    fn?.();
  };

  const menuLinks = [
    { label: 'All Books', action: onBooks },
    ...CATEGORY_PAGES.map((page) => ({ label: page.label, action: () => onCategory(page.slug), href: `/${page.slug}` }))
  ];

  return (
    <header className="storeHeader">
      <button className="logo-wrap" onClick={onStore}>
        {logoUrl ? (
          <>
            <img src={logoUrl} alt={name} className="logo-img" />
            <div className="logo-text">
              <span className="lt1">Muppadai Academy</span>
              <span className="lt2">Govt. Exam Books</span>
            </div>
          </>
        ) : (
          <>
            <div className="logo-mark"><span>M</span></div>
            <div className="logo-text">
              <span className="lt1">Muppadai Academy</span>
              <span className="lt2">Govt. Exam Books</span>
            </div>
          </>
        )}
      </button>
      <nav className="desktopStoreNav" aria-label="Store navigation">
        <button type="button" onClick={onBooks}>Books</button>
        <div className="storeNavDrop">
          <button type="button">
            Categories <ChevronDown size={13} />
          </button>
          <div className="storeNavMenu">
            {CATEGORY_PAGES.map((page) => (
              <a href={`/${page.slug}`} key={page.slug} onClick={(event) => { event.preventDefault(); onCategory(page.slug); }}>
                {page.shortLabel || page.label}
              </a>
            ))}
          </div>
        </div>
        <a href="/ebooks" onClick={(event) => { event.preventDefault(); onCategory('ebooks'); }}>E-Books</a>
        <button type="button" onClick={onTrack}>Track</button>
      </nav>
      <div className="hdr-right">
        <button className="menu-btn" type="button" onClick={() => setMenuOpen(true)} aria-label="Open menu">
          <Menu size={19} />
        </button>
        <button className="login-btn" onClick={onProfile}>
          {getOtpSessionPhone() ? 'Profile' : 'Login'}
        </button>
        <button className="cart-btn" onClick={onCart} title="Open cart">
          <ShoppingCart size={18} />
          <span className="cart-count">{cartCount}</span>
        </button>
      </div>
      {menuOpen && (
        <div className="mobileMenuLayer" onClick={(event) => event.target === event.currentTarget && setMenuOpen(false)}>
          <div className="mobileMenuPanel">
            <div className="mobileMenuHead">
              <strong>Menu</strong>
              <button type="button" onClick={() => setMenuOpen(false)}><X size={18} /></button>
            </div>
            <div className="mobileMenuLinks">
              {menuLinks.map((item) => (
                <a href={item.href || '/'} key={item.label} onClick={(event) => { event.preventDefault(); closeAndRun(item.action); }}>
                  <BookOpen size={16} />
                  <span>{item.label}</span>
                </a>
              ))}
              <button type="button" onClick={() => closeAndRun(onTrack)}>
                <Truck size={16} />
                <span>Track Order</span>
              </button>
              <button type="button" onClick={() => closeAndRun(onOrders)}>
                <Package size={16} />
                <span>My Orders</span>
              </button>
              <button type="button" onClick={() => closeAndRun(onProfile)}>
                <User size={16} />
                <span>{getOtpSessionPhone() ? 'Profile' : 'Login'}</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}

function StoreBottomNav({ cartCount, onCart, onBooks, onEbooks, onOrders, onStore }) {
  return (
    <nav className="mobileBottomNav">
      <button type="button" onClick={onStore}>
        <Home size={18} />
        <span>Home</span>
      </button>
      <button type="button" onClick={onBooks}>
        <BookOpen size={18} />
        <span>Books</span>
      </button>
      <button type="button" onClick={onEbooks}>
        <FileText size={18} />
        <span>E-Books</span>
      </button>
      <button type="button" onClick={onOrders}>
        <Package size={18} />
        <span>Orders</span>
      </button>
      <button type="button" onClick={onCart}>
        <span className="navCartIcon">
          <ShoppingCart size={18} />
          {!!cartCount && <b>{cartCount}</b>}
        </span>
        <span>Cart</span>
      </button>
    </nav>
  );
}

function ChatBubble({ phone, open, onOpen, onClose, onLogin }) {
  return open
    ? <ChatWidget phone={phone} onClose={onClose} onLogin={onLogin} />
    : (
      <button className="chatBubble" onClick={phone ? onOpen : onLogin} title={phone ? 'Chat with us' : 'Login to chat with support'}>
        <MessageCircle size={22} />
      </button>
    );
}

function ChatWidget({ phone, onClose, onLogin }) {
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const endRef = React.useRef(null);

  useEffect(() => {
    if (!phone) return;
    import('firebase/firestore').then(({ collection, onSnapshot, query, orderBy, limit }) => {
      const q = query(collection(db, 'chats', phone, 'messages'), orderBy('createdAt', 'asc'), limit(100));
      const unsub = onSnapshot(q, (snap) => {
        setMessages(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      }, () => {});
      return () => unsub();
    }).catch(() => {});
  }, [phone]);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  async function send() {
    if (!text.trim() || !phone || sending) return;
    setSending(true);
    const t = text.trim();
    setText('');
    try {
      const { collection, addDoc, doc, setDoc } = await import('firebase/firestore');
      await addDoc(collection(db, 'chats', phone, 'messages'), { text: t, sender: 'user', createdAt: new Date().toISOString(), read: false });
      await setDoc(doc(db, 'chats', phone), { lastMsg: t, lastMsgAt: new Date().toISOString(), unreadAdmin: 1, name: phone }, { merge: true });
    } catch (e) { console.error(e); }
    setSending(false);
  }

  return (
    <div className="chatWidget">
      <div className="chatWidgetHead">
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <div style={{ width: 8, height: 8, background: '#4ade80', borderRadius: '50%', animation: 'pulse 2s infinite' }} />
          <strong>Live Chat Support</strong>
        </div>
        <button type="button" onClick={onClose} style={{ background: 'rgba(255,255,255,0.2)', border: 'none', color: '#fff', width: 28, height: 28, borderRadius: '50%', cursor: 'pointer', fontSize: 16 }}>✕</button>
      </div>
      {!phone ? (
        <div style={{ padding: 24, textAlign: 'center', display: 'flex', flexDirection: 'column', gap: 14, alignItems: 'center' }}>
          <MessageCircle size={40} style={{ color: 'var(--navy)', opacity: 0.4 }} />
          <p style={{ fontSize: 13, color: 'var(--muted)', margin: 0 }}>Login to start a conversation with our support team.</p>
          <button className="primaryButton" onClick={() => { onClose(); onLogin(); }}>Login to Chat</button>
        </div>
      ) : (
        <>
          <div className="chatWidgetMessages">
            {!messages.length && <div style={{ textAlign: 'center', color: 'var(--muted)', fontSize: 12, padding: '28px 16px', lineHeight: 1.6 }}>👋 Hi! Send us a message and we'll respond shortly.</div>}
            {messages.map((msg) => (
              <div key={msg.id} style={{ display: 'flex', justifyContent: msg.sender === 'admin' ? 'flex-start' : 'flex-end' }}>
                <div className={msg.sender === 'admin' ? 'chatMsgAdmin' : 'chatMsgUser'}>
                  <div>{msg.text}</div>
                  <div style={{ fontSize: 10, opacity: 0.6, marginTop: 3, textAlign: msg.sender === 'admin' ? 'left' : 'right' }}>
                    {msg.createdAt ? new Date(msg.createdAt instanceof Object && msg.createdAt.toDate ? msg.createdAt.toDate() : msg.createdAt).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' }) : ''}
                  </div>
                </div>
              </div>
            ))}
            <div ref={endRef} />
          </div>
          <div className="chatWidgetInput">
            <input
              value={text}
              onChange={(e) => setText(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && (e.preventDefault(), send())}
              placeholder="Type a message…"
            />
            <button type="button" onClick={send} disabled={!text.trim() || sending}>Send</button>
          </div>
        </>
      )}
    </div>
  );
}

function Storefront({ books, loading, ratings, onOpenBook, onCategory }) {
  const [category, setCategory] = useState('all');

  const physicalBooks = useMemo(() => books.filter((book) => bookProductType(book) === 'physical'), [books]);
  const ebookBooks = useMemo(() => books.filter((book) => bookProductType(book) === 'ebook' || bookProductType(book) === 'combo'), [books]);
  const categories = useMemo(() => ['all', ...new Set(physicalBooks.map((book) => book.cat).filter(Boolean))], [physicalBooks]);
  const visibleBooks = useMemo(() => {
    return physicalBooks.filter((book) => category === 'all' || book.cat === category);
  }, [physicalBooks, category]);
  const groupedBooks = useMemo(() => {
    return visibleBooks.reduce((groups, book) => {
      const key = book.cat || 'Books';
      groups[key] = groups[key] || [];
      groups[key].push(book);
      return groups;
    }, {});
  }, [visibleBooks]);

  return (
    <>
      <section className="hero">
        <div className="hero-bg" />
        <div className="hero-content">
          <div className="hero-badge"><span />Tamil Nadu&apos;s Trusted Exam Book Store</div>
          <h1>Government Exam Books<br /><em>For Serious Preparation</em></h1>
          <p className="hero-sub">Study materials for TNUSRB, TNPSC, SSC GD, RRB and Indian Army, delivered across Tamil Nadu.</p>
          <div className="hero-pills">
            <button type="button" className="hero-pill" onClick={() => onCategory('tnusrb-si-books')}>TNUSRB SI</button>
            <button type="button" className="hero-pill" onClick={() => onCategory('tnusrb-pc-books')}>TNUSRB PC</button>
            <button type="button" className="hero-pill" onClick={() => onCategory('ssc-gd-books')}>SSC GD</button>
            <button type="button" className="hero-pill" onClick={() => onCategory('ebooks')}>E-Books</button>
          </div>
        </div>
      </section>

      <div className="stats-bar">
        <div className="stat-item"><div className="stat-num">50,000+</div><div className="stat-lbl">Students Served</div></div>
        <div className="stat-item"><div className="stat-num">{books.length || '20+'}</div><div className="stat-lbl">Book Series</div></div>
        <div className="stat-item"><div className="stat-num">{Math.max(categories.length - 1, 3)}</div><div className="stat-lbl">Exam Categories</div></div>
        <div className="stat-item"><div className="stat-num">2-3</div><div className="stat-lbl">Days Delivery</div></div>
      </div>

      <main className="catalog" id="catalog">
        <section className="seoCategoryStrip" aria-label="Popular book categories">
          {CATEGORY_PAGES.slice(0, 9).map((page) => (
            <a href={`/${page.slug}`} key={page.slug} onClick={(event) => { event.preventDefault(); onCategory(page.slug); }}>
              <span>{page.shortLabel || page.label}</span>
            </a>
          ))}
        </section>

        <div className="cat-tabs">
          {categories.map((item) => (
            <button className={category === item ? 'cat-tab active' : 'cat-tab'} onClick={() => setCategory(item)} key={item}>
              {item === 'all' ? 'All Books' : item === 'CENTRAL' ? 'Central Govt' : item}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="book-skeleton-grid">
            {Array.from({ length: 8 }).map((_, index) => (
              <div className="book-skeleton-card" key={index}><div className="skel-block" /><div className="skel-body"><div className="skel-line" /><div className="skel-line short" /><div className="skel-line tiny" /></div></div>
            ))}
          </div>
        ) : Object.keys(groupedBooks).length ? (
          Object.entries(groupedBooks).map(([cat, list]) => (
            <section key={cat}>
              <div className="sec-head"><h3>{cat === 'CENTRAL' ? 'Central Government Books' : `${cat} Books`}</h3><div className="sec-line" /></div>
              <div className="book-grid">
                {list.map((book) => (
                  <ProductCard key={book.id} book={book} rating={ratings[book.id]} onOpen={onOpenBook} />
                ))}
              </div>
            </section>
          ))
        ) : (
          <EmptyState title="No books found" body="Try another category." />
        )}

        {!loading && category === 'all' && ebookBooks.length > 0 && (
          <section className="ebookHomeSection">
            <div className="sec-head"><h3>E-Books</h3><div className="sec-line" /></div>
            <div className="book-grid">
              {ebookBooks.map((book) => (
                <ProductCard key={book.id} book={book} rating={ratings[book.id]} onOpen={onOpenBook} />
              ))}
            </div>
          </section>
        )}
      </main>

      <section className="features">
        <div className="features-inner">
          <div className="feat"><Truck size={24} /><div className="feat-title">Fast Courier Delivery</div><div className="feat-desc">Books delivered to your doorstep within 2-3 working days across Tamil Nadu</div></div>
          <div className="feat"><BookOpen size={24} /><div className="feat-title">Latest Syllabus</div><div className="feat-desc">Books updated for current exam patterns and syllabus changes</div></div>
          <div className="feat"><ShieldCheck size={24} /><div className="feat-title">Secure Payments</div><div className="feat-desc">Safe payments via Razorpay with UPI, cards, and net banking</div></div>
          <div className="feat"><Phone size={24} /><div className="feat-title">Order Support</div><div className="feat-desc">Order confirmations and delivery updates for every purchase</div></div>
        </div>
      </section>


    </>
  );
}

function SiteFooter({ onNavigate, settings }) {
  function openPage(event, slug) {
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    event.preventDefault();
    onNavigate(slug);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  return (
    <footer style={{ display: 'grid', justifyItems: 'center', gap: 10, borderTop: '1px solid var(--line)' }}>
      <nav aria-label="Store information" style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: '8px 18px' }}>
        {SITE_PAGE_DEFINITIONS.map((page) => (
          <a key={page.slug} href={`/${page.slug}`} onClick={(event) => openPage(event, page.slug)}>{sitePageContent(settings, page.slug).label || page.title}</a>
        ))}
      </nav>
      <span>© 2026 Muppadai Training Academy. All rights reserved.</span>
    </footer>
  );
}

function PolicyPage({ page, slug }) {
  const isContact = slug === 'contact-us';
  const phoneHref = String(page.phone || '').replace(/[^+\d]/g, '');

  return (
    <main style={{ minHeight: '62vh' }}>
      <section style={{ padding: '56px 20px 42px', textAlign: 'center', background: 'var(--paper)', borderBottom: '1px solid var(--line)' }}>
        <span style={{ color: 'var(--navy)', fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.08em' }}>{isContact ? 'Get in touch' : 'Store Policy'}</span>
        <h1 style={{ margin: '10px 0 8px', color: 'var(--ink)', fontSize: 32 }}>{page.heading}</h1>
        <p style={{ margin: 0, color: 'var(--text3)', fontSize: 14 }}>Muppadai Training Academy Books</p>
      </section>
      <section style={{ width: 'min(760px, calc(100% - 32px))', margin: '0 auto', padding: '42px 0 56px' }}>
        {isContact && (page.email || page.phone || page.address) && (
          <div className="policyContactDetails">
            {page.email && <p><strong>Email</strong><a href={`mailto:${page.email}`}>{page.email}</a></p>}
            {page.phone && <p><strong>Phone</strong><a href={`tel:${phoneHref}`}>{page.phone}</a></p>}
            {page.address && <p><strong>Address</strong><span>{page.address}</span></p>}
          </div>
        )}
        {page.sections.map((section, index) => (
          <div key={index} style={{ marginTop: index === 0 ? 0 : 30 }}>
            <h2 style={{ margin: '0 0 8px', color: 'var(--ink)', fontSize: 20 }}>{section.heading}</h2>
            <p style={{ margin: 0, color: 'var(--text2)', fontSize: 15, lineHeight: 1.75, whiteSpace: 'pre-wrap' }}>{section.body}</p>
          </div>
        ))}
      </section>
    </main>
  );
}
function CategoryLandingPage({ page, books, loading, ratings, onOpenBook, onCategory }) {
  const matchedBooks = useMemo(() => books.filter((book) => categoryMatchesBook(page, book)), [books, page]);
  const siblingPages = CATEGORY_PAGES.filter((item) => item.slug !== page.slug).slice(0, 8);
  const ebookPage = page.type === 'ebook';

  return (
    <main className="categoryPage">
      <section className={ebookPage ? 'categoryHero ebookHero' : 'categoryHero'}>
        <div>
          <span className="categoryEyebrow">{ebookPage ? 'Digital Study Materials' : 'Exam Book Category'}</span>
          <h1>{page.label}</h1>
          <p>{page.intro || page.description}</p>
          <div className="categoryHeroActions">
            <a href="#category-products">View Books</a>
            <button type="button" onClick={() => onCategory('ebooks')}>Explore E-Books</button>
          </div>
        </div>
        <div className="categoryHeroCard" aria-hidden="true">
          <BookOpen size={30} />
          <strong>{matchedBooks.length || 'New'}</strong>
          <span>{ebookPage ? 'digital materials' : 'book options'}</span>
        </div>
      </section>

      <section className="categoryTrust">
        <span><Truck size={16} /> Courier delivery for physical books</span>
        <span><ShieldCheck size={16} /> Secure payment</span>
        <span><FileText size={16} /> Sample previews where available</span>
      </section>

      <section className="categoryBody" id="category-products">
        <div className="detailSectionHead">
          <h2>{page.label}</h2>
          <span>{matchedBooks.length} product{matchedBooks.length !== 1 ? 's' : ''}</span>
        </div>
        {loading ? (
          <div className="book-skeleton-grid">
            {Array.from({ length: 6 }).map((_, index) => (
              <div className="book-skeleton-card" key={index}><div className="skel-block" /><div className="skel-body"><div className="skel-line" /><div className="skel-line short" /></div></div>
            ))}
          </div>
        ) : matchedBooks.length ? (
          <div className="book-grid">
            {matchedBooks.map((book) => (
              <ProductCard key={book.id} book={book} rating={ratings[book.id]} onOpen={onOpenBook} />
            ))}
          </div>
        ) : (
          <EmptyState
            title={ebookPage ? 'E-books are coming soon' : 'No active books in this category'}
            body={ebookPage ? 'Add e-book details from the admin book editor to publish them here.' : 'This category will show books automatically when active matching products are added.'}
          />
        )}
      </section>

      {!!page.faqs?.length && (
        <section className="categoryBody categoryFaq">
          <div className="detailSectionHead">
            <h2>Student Questions</h2>
            <span>SEO FAQ</span>
          </div>
          <div className="faqList">
            {page.faqs.map(([q, a]) => (
              <details key={q}>
                <summary>{q}</summary>
                <p>{a}</p>
              </details>
            ))}
          </div>
        </section>
      )}

      <section className="categoryBody categoryLinks">
        <div className="detailSectionHead">
          <h2>Explore More Books</h2>
          <span>Categories</span>
        </div>
        <div className="seoCategoryStrip compact">
          {siblingPages.map((item) => (
            <a href={`/${item.slug}`} key={item.slug} onClick={(event) => { event.preventDefault(); onCategory(item.slug); }}>
              <span>{item.label}</span>
            </a>
          ))}
        </div>
      </section>
    </main>
  );
}

function ProductCard({ book, rating, onOpen }) {
  const inStockVariants = (book.variants || []).filter((variant) => variant.inStock !== false);
  const bookOOS = book.inStock === false || !inStockVariants.length;
  const prices = (bookOOS ? book.variants || [] : inStockVariants).map((variant) => Number(variant.price || 0)).filter(Boolean);
  const min = prices.length ? Math.min(...prices) : 0;
  const max = prices.length ? Math.max(...prices) : 0;
  const type = bookProductType(book);

  return (
    <a className="bcard" href={`/book/${bookSlug(book)}`} onClick={(event) => { event.preventDefault(); onOpen(book); }}>
      <div className="bcard-img">
        {book.imageUrl ? <img src={book.imageUrl} alt={book.title || 'Book'} /> : <BookOpen size={34} />}
        <span className="bcard-cat">{book.cat || 'BOOK'}</span>
        {type !== 'physical' && <span className="bcard-type">{type === 'combo' ? 'Book + E-Book' : 'E-Book'}</span>}
        {bookOOS && <span className="bcard-oos">Out of Stock</span>}
      </div>
      <div className="bcard-body">
        <div className="bcard-title">{book.title || 'Untitled book'}</div>
        {!!rating?.count && <div className="bookRatingLine">★ {Number(rating.average || 0).toFixed(1)} ({rating.count})</div>}
        <div className="bcard-price">
          {bookOOS ? <span className="oos-badge">Out of stock</span> : <><span className="from">Starting from</span>{money(min)}{min !== max ? ` - ${money(max)}` : ''}</>}
        </div>
        <div className="bcard-meta">
          <span className="bcard-vars">{book.variants?.length || 0} variant{(book.variants?.length || 0) !== 1 ? 's' : ''}</span>
          <div className="bcard-arrow">-&gt;</div>
        </div>
      </div>
    </a>
  );
}

function BookDetailPage({ book, books = [], loading, ratings, onAdd, onBack, onProfile, onOpenBook }) {
  const [qtys, setQtys] = useState({});
  if (loading) return <main className="bookDetailPage"><div className="loading">Loading book...</div></main>;
  if (!book) return <main className="bookDetailPage"><EmptyState title="Book not found" body="This book may be unavailable or removed." /><button className="primaryButton" onClick={onBack}>Back to Books</button></main>;

  const rating = ratings[book.id] || {};
  const bookOOS = book.inStock === false;
  const totalQty = Object.values(qtys).reduce((sum, value) => sum + Number(value || 0), 0);
  const points = bookDetailPoints(book);
  const samples = bookSampleResources(book);
  const faqs = bookFaqs(book);
  const relatedBooks = books
    .filter((item) => item.id !== book.id && item.cat === book.cat)
    .slice(0, 4);
  const loggedInPhone = getOtpSessionPhone();
  const productType = bookProductType(book);
  const isEbookOnly = productType === 'ebook';

  function setQty(index, next) {
    if (isEbookOnly) {
      setQtys((current) => (current[index] ? {} : { [index]: 1 }));
      return;
    }
    setQtys((current) => ({ ...current, [index]: Math.max(0, Number(next || 0)) }));
  }

  function addSelected() {
    (book.variants || []).forEach((variant, index) => {
      const qty = isEbookOnly && qtys[index] ? 1 : Number(qtys[index] || 0);
      if (qty > 0 && variant.inStock !== false && !bookOOS) onAdd(book, variant, qty);
    });
  }

  return (
    <main className="bookDetailPage">
      <button type="button" className="detailBackButton" onClick={onBack}>
        <ArrowLeft size={16} />
        Back to Books
      </button>
      <nav className="breadcrumb"><a href="/" onClick={(e) => { e.preventDefault(); onBack(); }}>Books</a><span>/</span><strong>{book.title}</strong></nav>
      <section className="bookDetailHero">
        <div className="bookDetailImage">
          {book.imageUrl ? <img src={book.imageUrl} alt={book.title} /> : <BookOpen size={58} />}
        </div>
        <div className="bookDetailInfo">
          <span className="detailCat">{book.cat || 'BOOK'}</span>
          <h1>{book.title}</h1>
          <div className="detailMetaRow">
            {!!rating.count ? (
              <div className="detailRating">★ {Number(rating.average || 0).toFixed(1)} <span>({rating.count} verified)</span></div>
            ) : (
              <div className="detailRating mutedRating">Verified ratings soon</div>
            )}
            <span className={bookOOS ? 'detailStock out' : 'detailStock'}>{bookOOS ? 'Out of stock' : 'In stock'}</span>
            {productType !== 'physical' && <span className="detailTypeBadge">{productType === 'combo' ? 'Book + E-Book' : 'E-Book'}</span>}
          </div>
          <div className="detailPrice"><span>Starting from</span>{money(bookPrice(book))}</div>
          <div className="seoTrustRow desktopTrustRow">
            <span><b className="trustIcon secure">₹</b>Secure Payment</span>
            <span>
              <b className={productType === 'ebook' ? 'trustIcon digital' : 'trustIcon courier'}>
                {productType === 'ebook' ? <FileText size={12} /> : '🚚'}
              </b>
              {productType === 'ebook' ? 'Digital Access Details' : 'Free Courier Delivery'}
            </span>
            <span><b className="trustIcon rating">★</b>Verified Student Ratings</span>
          </div>

          <section className="detailBuyBox" aria-label="Select variants and quantity">
            <div className="detailSectionHead">
              <h2>{isEbookOnly ? 'Select E-Book' : 'Select Variants & Quantity'}</h2>
              <span>{book.variants?.length || 0} option{(book.variants?.length || 0) !== 1 ? 's' : ''}</span>
            </div>
            <div className="detailVariants">
              {(book.variants || []).map((variant, index) => {
                const disabled = bookOOS || variant.inStock === false;
                return (
                  <div className={[disabled ? 'detailVariant oos' : 'detailVariant', qtys[index] ? 'selected' : ''].filter(Boolean).join(' ')} key={`${variant.name}-${index}`}>
                    <div className="detailVariantImg">{variant.imageUrl || book.imageUrl ? <img src={variant.imageUrl || book.imageUrl} alt="" /> : <BookOpen size={18} />}</div>
                    <div className="detailVariantInfo">
                      <div className="detailVariantName">{variant.name || 'Default'}</div>
                      <div className="detailVariantPrice">{disabled ? 'Out of stock' : money(variant.price)}</div>
                    </div>
                    {isEbookOnly ? (
                      <button
                        type="button"
                        className={qtys[index] ? 'ebookSelectBtn selected' : 'ebookSelectBtn'}
                        disabled={disabled}
                        onClick={() => setQty(index, 1)}
                      >
                        {qtys[index] ? 'Selected' : 'Select'}
                      </button>
                    ) : (
                      <div className="qtyPick detailQty">
                        <button disabled={disabled || !qtys[index]} onClick={() => setQty(index, Number(qtys[index] || 0) - 1)} aria-label="Decrease quantity">-</button>
                        <span>{qtys[index] || 0}</span>
                        <button disabled={disabled} onClick={() => setQty(index, Number(qtys[index] || 0) + 1)} aria-label="Increase quantity">+</button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            <button className="btn-addcart detailAdd" disabled={!totalQty} onClick={addSelected}>{isEbookOnly ? 'Add E-Book to Cart' : 'Add Selected to Cart'}</button>
            <div className="seoTrustRow mobileTrustRow">
              <span><b className="trustIcon secure">₹</b>Secure Payment</span>
              <span>
                <b className={productType === 'ebook' ? 'trustIcon digital' : 'trustIcon courier'}>
                  {productType === 'ebook' ? <FileText size={12} /> : '🚚'}
                </b>
                {productType === 'ebook' ? 'Digital Access' : 'Free Courier Delivery'}
              </span>
              <span><b className="trustIcon rating">★</b>Verified Student Ratings</span>
            </div>
          </section>
        </div>
      </section>

      {book.ebook?.enabled && (
        <section className="bookDetailPanel ebookInfoPanel">
          <div className="detailSectionHead">
            <h2>E-Book Details</h2>
            <span>{book.ebook.format || 'PDF'}</span>
          </div>
          <div className="ebookInfoGrid">
            <div><FileText size={17} /><strong>Format</strong><span>{book.ebook.format || 'PDF / Digital material'}</span></div>
            <div><BookOpen size={17} /><strong>Pages</strong><span>{book.ebook.pages ? `${book.ebook.pages} pages` : 'Mentioned in sample preview'}</span></div>
            <div><ShieldCheck size={17} /><strong>Access</strong><span>{book.ebook.deliveryMode || 'Shared after payment verification'}</span></div>
          </div>
          {(book.ebook.accessNote || book.ebook.licenseText) && (
            <p className="ebookInfoNote">{book.ebook.accessNote || book.ebook.licenseText}</p>
          )}
          {book.ebook.sampleUrl && (
            <a className="ebookSampleLink" href={book.ebook.sampleUrl} target="_blank" rel="noreferrer">
              <FileText size={16} />
              View e-book sample
            </a>
          )}
        </section>
      )}

      {samples.length > 0 && (
        <section className="bookDetailPanel samplePreviewPanel">
          <div className="detailSectionHead">
            <h2>Sample Book Preview</h2>
            <span>{samples.length} page{samples.length !== 1 ? 's' : ''}</span>
          </div>
          <div className={samples.every((sample) => sample.type === 'pdf') ? 'samplePreviewGrid pdfOnly' : 'samplePreviewGrid'}>
            {samples.map((sample, index) => (
              <a className={sample.type === 'pdf' ? 'samplePreviewCard samplePdfCard' : 'samplePreviewCard'} href={sample.url} target="_blank" rel="noreferrer" key={`${sample.url}-${index}`}>
                {sample.type === 'pdf' ? (
                  <div className="samplePdfIcon" aria-hidden="true"><span>PDF</span></div>
                ) : (
                  <img src={sample.url} alt={`${book.title} sample page ${index + 1}`} loading="lazy" />
                )}
                <span>{sample.label}</span>
              </a>
            ))}
          </div>
        </section>
      )}

      {points.length > 0 && (
        <section className="bookDetailPanel bookPointsPanel">
          <div className="detailSectionHead">
            <h2>Book Details</h2>
            <span>What students get</span>
          </div>
          <ul className="detailPointList">
            {points.map((point, index) => (
              <li key={`${point}-${index}`}>
                <Check size={16} />
                <span>{point}</span>
              </li>
            ))}
          </ul>
        </section>
      )}

      {faqs.length > 0 && (
        <section className="bookDetailPanel faqPanel">
          <div className="detailSectionHead">
            <h2>Student Questions</h2>
            <span>Quick answers</span>
          </div>
          <div className="faqList">
            {faqs.map((item, index) => (
              <details key={`${item.q}-${index}`}>
                <summary>{item.q}</summary>
                <p>{item.a}</p>
              </details>
            ))}
          </div>
        </section>
      )}

      <section className="bookDetailPanel reviewPanel">
        <div className="detailSectionHead">
          <h2>Verified Student Reviews</h2>
          {!!rating.count && <span>{Number(rating.average || 0).toFixed(1)} average rating</span>}
        </div>
        {rating.reviews?.length ? (
          <div className="reviewGrid">
            {rating.reviews.slice(0, 8).map((item) => (
              <div className="reviewCard" key={item.id}>
                <strong>{'★'.repeat(Number(item.rating || 0))}</strong>
                <p>{item.review || 'Verified purchase rating'}</p>
                <span>Verified student</span>
              </div>
            ))}
          </div>
        ) : <p className="mutedText">Ratings will appear here after students purchase and review this book.</p>}
        <div className="reviewLoginNote">
          <ShieldCheck size={17} />
          <span>{loggedInPhone ? 'You can review purchased books from your order history after delivery.' : 'Login after purchase to review this book.'}</span>
          <button type="button" onClick={onProfile}>{loggedInPhone ? 'Open Orders' : 'Login'}</button>
        </div>
      </section>

      {relatedBooks.length > 0 && (
        <section className="bookDetailPanel relatedBooksPanel">
          <div className="detailSectionHead">
            <h2>Related Books</h2>
            <span>{book.cat}</span>
          </div>
          <div className="relatedBooksGrid">
            {relatedBooks.map((item) => (
              <ProductCard key={item.id} book={item} rating={ratings[item.id]} onOpen={onOpenBook} />
            ))}
          </div>
        </section>
      )}
    </main>
  );
}

function BookSheet({ book, onClose, onAdd }) {
  const [qtys, setQtys] = useState({});
  const bookOOS = book.inStock === false;
  const totalQty = Object.values(qtys).reduce((sum, value) => sum + Number(value || 0), 0);

  function setQty(index, next) {
    setQtys((current) => ({ ...current, [index]: Math.max(0, Number(next || 0)) }));
  }

  function addSelected() {
    (book.variants || []).forEach((variant, index) => {
      const qty = Number(qtys[index] || 0);
      if (qty > 0 && variant.inStock !== false && !bookOOS) onAdd(book, variant, qty);
    });
    onClose();
  }

  return (
    <div className="overlay" onClick={(event) => event.target === event.currentTarget && onClose()}>
      <div className="sheet">
        <div className="sheet-handle" />
        <div className="sheet-head">
          <button className="backRound" onClick={onClose}>Back</button>
          <h2>{book.title}</h2>
        </div>
        {book.description && <div className="sheet-desc" dangerouslySetInnerHTML={{ __html: book.description }} />}
        <div className="sheet-sec-label">Select Variants & Quantity</div>
        <div className="vlist">
          {(book.variants || []).map((variant, index) => {
            const disabled = bookOOS || variant.inStock === false;
            return (
              <div className={disabled ? 'vi oos' : 'vi'} key={`${variant.name}-${index}`}>
                <div className="vi-img">{variant.imageUrl || book.imageUrl ? <img src={variant.imageUrl || book.imageUrl} alt="" /> : <BookOpen size={18} />}</div>
                <div className="vi-info">
                  <div className="vi-name">{variant.name || 'Default'}</div>
                  <div className="vi-price">{disabled ? 'Out of stock' : money(variant.price)}</div>
                </div>
                <div className="qtyPick">
                  <button disabled={disabled || !qtys[index]} onClick={() => setQty(index, Number(qtys[index] || 0) - 1)}>-</button>
                  <span>{qtys[index] || 0}</span>
                  <button disabled={disabled} onClick={() => setQty(index, Number(qtys[index] || 0) + 1)}>+</button>
                </div>
              </div>
            );
          })}
        </div>
        <div className="sheet-foot">
          <button className="btn-addcart" disabled={!totalQty} onClick={addSelected}>Add to Cart</button>
        </div>
      </div>
    </div>
  );
}

function CartDrawer({ open, cart, onClose, onAddMore, onQty, onRemove, onClear, onCheckoutSuccess }) {
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const total = cart.reduce((sum, item) => {
    const isEbookItem = String(item.productType || '').toLowerCase() === 'ebook';
    return sum + Number(item.price || 0) * (isEbookItem ? 1 : Number(item.qty || 1));
  }, 0);

  if (!open) return null;

  return (
    <div className="drawerLayer">
      <button className="drawerScrim" onClick={onClose} aria-label="Close cart" />
      <aside className="drawer">
        <div className="drawerHeader">
          <h2>Cart</h2>
          <button className="iconButton" onClick={onClose} title="Close cart">
            x
          </button>
        </div>
        <div className="drawerBody">
          {!cart.length ? (
            <EmptyState title="Cart is empty" body="Add books from the store to start an order." />
          ) : (
            cart.map((item) => {
              const isEbookItem = String(item.productType || '').toLowerCase() === 'ebook';
              const displayQty = isEbookItem ? 1 : Number(item.qty || 1);
              return (
                <div className="cartLine" key={item.key}>
                  <div className="miniImage">{item.img ? <img src={item.img} alt="" /> : <BookOpen size={22} />}</div>
                  <div>
                    <strong>{item.bt}</strong>
                    <span>{item.vn}</span>
                    <div className="qtyRow">
                      {isEbookItem ? (
                        <span className="ebookCartSingle">1 digital copy</span>
                      ) : (
                        <>
                          <button onClick={() => onQty(item.key, -1)}>-</button>
                          <span>{displayQty}</span>
                          <button onClick={() => onQty(item.key, 1)}>+</button>
                        </>
                      )}
                      <button className="linkButton" onClick={() => onRemove(item.key)}>
                        Remove
                      </button>
                    </div>
                  </div>
                  <b>{money(Number(item.price || 0) * displayQty)}</b>
                </div>
              );
            })
          )}
        </div>
        <div className="drawerFooter">
          <div className="totalRow">
            <span>Total</span>
            <strong>{money(total)}</strong>
          </div>
          <button className="primaryButton wide" disabled={!cart.length} onClick={() => setCheckoutOpen(true)}>
            Checkout
          </button>
          <button className="secondaryButton wide" type="button" onClick={onAddMore}>
            Add more books
          </button>
          <button className="secondaryButton wide" disabled={!cart.length} onClick={onClear}>
            Clear cart
          </button>
        </div>
      </aside>
      {checkoutOpen && <CheckoutPanel cart={cart} onClose={() => setCheckoutOpen(false)} onSuccess={onCheckoutSuccess} />}
    </div>
  );
}

function CheckoutPanel({ cart, onClose, onSuccess }) {
  const savedPhone = getOtpSessionPhone() || getCustomerPhone();
  const [form, setForm] = useState({
    name: '',
    phone: savedPhone,
    email: '',
    address: '',
    altPhone: '',
    street: '',
    postOffice: '',
    pincode: '',
    district: '',
    state: 'Tamil Nadu'
  });
  const [verified, setVerified] = useState(() => isValidIndianMobile(savedPhone) && getOtpSessionPhone() === savedPhone);
  const [authStep, setAuthStep] = useState(() => (isValidIndianMobile(savedPhone) && getOtpSessionPhone() === savedPhone ? 'verified' : 'phone'));
  const [otp, setOtp] = useState('');
  const [otpSeconds, setOtpSeconds] = useState(0);
  const [pinStatus, setPinStatus] = useState('');
  const [postOffices, setPostOffices] = useState([]);
  const [addressMode, setAddressMode] = useState('saved');
  const [busy, setBusy] = useState(false);
  const [otpBusy, setOtpBusy] = useState(false);
  const [pinBusy, setPinBusy] = useState(false);
  const [message, setMessage] = useState('');

  function setField(key, value) {
    const nextValue = ['phone', 'altPhone'].includes(key)
      ? cleanPhone(value)
      : key === 'pincode'
        ? String(value || '').replace(/\D/g, '').slice(0, 6)
        : value;
    setForm((current) => ({ ...current, [key]: nextValue }));
  }

  function applyPrefill(student, lastOrder) {
    const delivery = lastOrder?.delivery || {};
    setForm((current) => {
      const next = {
        ...current,
        name: student?.name || delivery.name || current.name,
        email: student?.email || delivery.email || current.email,
        altPhone: cleanPhone(student?.altPhone || delivery.altPhone || current.altPhone),
        street: student?.street || delivery.street || current.street,
        postOffice: student?.postOffice || delivery.postOffice || current.postOffice || '',
        pincode: String(student?.pincode || delivery.pincode || current.pincode || '').replace(/\D/g, '').slice(0, 6),
        district: student?.district || delivery.district || current.district,
        state: student?.state || delivery.state || current.state || 'Tamil Nadu'
      };
      next.address = student?.address || delivery.address || buildAddress(next) || current.address;
      return next;
    });
  }

  async function loadSavedProfile(phone) {
    const clean = cleanPhone(phone);
    if (!clean) return;
    try {
      const { student, lastOrder } = await loadStudentProfileWithLastOrder(clean);
      applyPrefill(student, lastOrder);
    } catch {
      // Prefill is helpful, but checkout should not fail because of it.
    }
  }

  async function requestOtp(resend = false) {
    const phone = cleanPhone(form.phone);
    if (!isValidIndianMobile(phone)) {
      setMessage('Enter a valid 10-digit mobile number.');
      return;
    }
    setOtpBusy(true);
    setMessage('');
    try {
      if (resend) await resendOtp(phone);
      else await sendOtp(phone);
      setAuthStep('otp');
      setOtp('');
      setOtpSeconds(60);
      setMessage(resend ? 'New OTP sent.' : 'OTP sent to +91 ' + phone);
    } catch (err) {
      setMessage(err.message || 'Could not send OTP. Please try again.');
    } finally {
      setOtpBusy(false);
    }
  }

  async function confirmOtp() {
    const phone = cleanPhone(form.phone);
    setOtpBusy(true);
    setMessage('');
    try {
      await verifyOtp(phone, otp);
      setVerified(true);
      setAuthStep('verified');
      setCustomerPhone(phone);
      setOtpSessionPhone(phone);
      setMessage('Mobile verified. Saved delivery details loaded if available.');
      await loadSavedProfile(phone);
      setAddressMode('saved');
    } catch (err) {
      setMessage(err.message || 'OTP verification failed.');
    } finally {
      setOtpBusy(false);
    }
  }

  async function submit(event) {
    event.preventDefault();
    const phone = cleanPhone(form.phone);
    if (!verified || getOtpSessionPhone() !== phone) {
      setMessage('Please verify your mobile number with OTP first.');
      return;
    }
    if (!form.name.trim() || !isValidIndianMobile(phone) || !form.street.trim()) {
      setMessage('Name, mobile number, and address are required.');
      return;
    }
    if (form.altPhone && !isValidIndianMobile(form.altPhone)) {
      setMessage('Alternate mobile must be a valid 10-digit number.');
      return;
    }
    if (form.pincode.length !== 6 || !form.district.trim() || !form.state.trim()) {
      setMessage('Enter a valid pincode so district and state can be filled.');
      return;
    }
    setBusy(true);
    setMessage('');
    try {
      const delivery = { ...form, phone, address: buildAddress(form) || form.address };
      await saveStudentAddress(phone, delivery);
      setCustomerPhone(phone);
      const draft = await createCheckoutDraft({ delivery, cart });
      await startRazorpayPayment({
        cart,
        delivery,
        draft,
        onSuccess: (order) => {
          setMessage(`Order created: ${order.orderId || order.id}`);
          onSuccess?.(order);
        },
        onFailure: (reason) => setMessage(reason)
      });
    } catch (err) {
      setMessage(err.message || 'Checkout failed. Please try again.');
    } finally {
      setBusy(false);
    }
  }

  async function saveAddressOnly() {
    const phone = cleanPhone(form.phone);
    if (!form.name.trim() || !isValidIndianMobile(phone) || !form.street.trim() || form.pincode.length !== 6 || !form.district || !form.state) {
      setMessage('Complete name, address, pincode, district and state before saving.');
      return;
    }
    setBusy(true);
    setMessage('');
    try {
      const delivery = { ...form, phone, address: buildAddress(form) || form.address };
      await saveStudentAddress(phone, delivery);
      setAddressMode('saved');
      setMessage('Address saved.');
    } catch (err) {
      setMessage(err.message || 'Could not save address.');
    } finally {
      setBusy(false);
    }
  }

  useEffect(() => {
    if (!otpSeconds) return undefined;
    const timer = setInterval(() => setOtpSeconds((current) => Math.max(0, current - 1)), 1000);
    return () => clearInterval(timer);
  }, [otpSeconds]);

  useEffect(() => {
    if (verified && form.phone) loadSavedProfile(form.phone);
  }, []);

  useEffect(() => {
    const pin = form.pincode;
    setPostOffices([]);
    if (pin.length !== 6) {
      setPinStatus('');
      return undefined;
    }
    setPinBusy(true);
    setPinStatus('Checking pincode...');
    const timer = setTimeout(async () => {
      try {
        const result = await lookupPincode(pin);
        setPostOffices(result.offices);
        setForm((current) => ({ ...current, district: result.district, state: result.state || 'Tamil Nadu' }));
        setPinStatus(result.district + ', ' + result.state);
      } catch (err) {
        setPinStatus(err.message || 'Pincode lookup failed.');
        setForm((current) => ({ ...current, district: '', state: '' }));
      } finally {
        setPinBusy(false);
      }
    }, 400);
    return () => clearTimeout(timer);
  }, [form.pincode]);

  const savedAddress = buildAddress(form);
  const hasSavedAddress = verified && Boolean(form.name && savedAddress && form.pincode && form.district && form.state);
  const showAddressForm = verified && (!hasSavedAddress || addressMode === 'form');

  return (
    <div className="modalLayer">
      <form className="checkoutPanel" onSubmit={submit}>
        <div className="drawerHeader">
          <h2>Delivery Details</h2>
          <button type="button" className="iconButton" onClick={onClose} title="Close checkout">
            x
          </button>
        </div>
        <div className="otpBox">
          <div className="checkoutSteps">
            <span className="done">1 Mobile</span>
            <i />
            <span className={verified ? 'active' : ''}>2 Address</span>
          </div>
          <div className="fieldWithIcon full">
            <span className="fieldIcon phone"><Phone size={18} /></span>
            <label>
              Mobile Number
              <div className="phoneInputWrap">
                <span>+91</span>
                <input
                  inputMode="numeric"
                  maxLength={10}
                  value={form.phone}
                  onChange={(event) => {
                    setField('phone', event.target.value);
                    setVerified(false);
                    setAuthStep('phone');
                    setOtp('');
                  }}
                  placeholder="Enter number"
                  disabled={busy}
                />
              </div>
            </label>
          </div>
          {authStep === 'phone' && (
            <button className="primaryButton wide" type="button" onClick={() => requestOtp(false)} disabled={otpBusy || busy}>
              <ShieldCheck size={16} />
              {otpBusy ? 'Sending OTP...' : 'Send OTP'}
            </button>
          )}
          {authStep === 'otp' && (
            <div className="otpVerifyRow">
              <input inputMode="numeric" maxLength={6} value={otp} onChange={(event) => setOtp(event.target.value.replace(/\D/g, '').slice(0, 6))} placeholder="6-digit OTP" />
              <button className="primaryButton" type="button" onClick={confirmOtp} disabled={otpBusy || otp.length !== 6}>
                Verify
              </button>
              <button className="secondaryButton" type="button" onClick={() => requestOtp(true)} disabled={otpBusy || otpSeconds > 0}>
                {otpSeconds ? `Resend ${otpSeconds}s` : 'Resend'}
              </button>
            </div>
          )}
          {verified && (
            <div className="verifiedStrip">
              <Check size={16} />
              Verified +91 {cleanPhone(form.phone)}
            </div>
          )}
        </div>
        {verified && (
          <div className="addressStage">
            <div className="mobileSummary">
              <Phone size={16} />
              <span>+91 {cleanPhone(form.phone)}</span>
              <button type="button" onClick={() => {
                setVerified(false);
                setAuthStep('phone');
                setOtp('');
              }}>
                Not you?
              </button>
            </div>
            {hasSavedAddress && addressMode !== 'form' && (
              <div className="savedAddressCard">
                <div className="radioDot" />
                <div>
                  <div className="savedAddressName">
                    {form.name}
                    <span>Home</span>
                  </div>
                  <p>{savedAddress}</p>
                  <small>+91 {cleanPhone(form.phone)}</small>
                  <div className="deliveryLine">
                    <Truck size={16} />
                    <span>Standard Delivery - 2-3 days</span>
                    <b>FREE</b>
                  </div>
                </div>
              </div>
            )}
            {hasSavedAddress && addressMode !== 'form' && (
              <button className="addAddressButton" type="button" onClick={() => setAddressMode('form')}>+ Add / Edit Address</button>
            )}
          </div>
        )}
        {showAddressForm && <div className="formGrid checkoutAddressGrid">
          <CheckoutField icon={<User size={18} />} tone="navy" label="Full Name">
            <input value={form.name} onChange={(event) => setField('name', event.target.value)} disabled={!verified || busy} />
          </CheckoutField>
          <CheckoutField icon={<Mail size={18} />} tone="blue" label="Email">
            <input type="email" value={form.email} onChange={(event) => setField('email', event.target.value)} disabled={!verified || busy} />
          </CheckoutField>
          <CheckoutField icon={<Phone size={18} />} tone="green" label="Alternate Mobile">
            <div className="phoneInputWrap">
              <span>+91</span>
              <input inputMode="numeric" maxLength={10} value={form.altPhone} onChange={(event) => setField('altPhone', event.target.value)} disabled={!verified || busy} placeholder="Optional" />
            </div>
          </CheckoutField>
          <CheckoutField icon={<Hash size={18} />} tone="gold" label="Pincode">
            <div className="pinInputWrap">
              <input inputMode="numeric" maxLength={6} value={form.pincode} onChange={(event) => setField('pincode', event.target.value)} disabled={!verified || busy} placeholder="600026" />
              {pinBusy && <span className="miniSpin" />}
            </div>
          </CheckoutField>
          <CheckoutField icon={<Home size={18} />} tone="red" label="Door No / Street / Area" className="full">
            <textarea value={form.street} onChange={(event) => setField('street', event.target.value)} disabled={!verified || busy} placeholder="Door No, Street, Area" />
          </CheckoutField>
          {!!postOffices.length && (
            <CheckoutField icon={<MapPin size={18} />} tone="blue" label="Post Office / Area" className="full">
              <select
                value={form.postOffice}
                onChange={(event) => {
                  setField('postOffice', event.target.value);
                }}
                disabled={!verified || busy}
              >
                <option value="">Select your area...</option>
                {postOffices.map((office) => (
                  <option value={office.Name} key={office.Name}>
                    {office.Name} ({office.BranchType})
                  </option>
                ))}
              </select>
            </CheckoutField>
          )}
          <CheckoutField icon={<Building2 size={18} />} tone="navy" label="District">
            <input value={form.district} onChange={(event) => setField('district', event.target.value)} disabled={!verified || busy} readOnly={Boolean(pinStatus && !pinStatus.includes('failed'))} />
          </CheckoutField>
          <CheckoutField icon={<MapPin size={18} />} tone="green" label="State">
            <input value={form.state} onChange={(event) => setField('state', event.target.value)} disabled={!verified || busy} readOnly={Boolean(pinStatus && !pinStatus.includes('failed'))} />
          </CheckoutField>
          <div className="addressFormActions">
            <button className="secondaryButton" type="button" onClick={saveAddressOnly} disabled={busy}>
              Save Address
            </button>
          </div>
        </div>}
        {pinStatus && <div className={'pinStatus ' + (form.district && form.state ? 'ok' : 'bad')}>{pinStatus}</div>}
        {message && <div className="banner compact">{message}</div>}
        <div className="checkoutActionBar">
          <div>
            <span>Total</span>
            <strong>{money(cart.reduce((sum, item) => sum + item.price * item.qty, 0))}</strong>
          </div>
          <button className="primaryButton" disabled={busy || !verified}>
            {busy ? 'Opening payment...' : 'Checkout'}
          </button>
        </div>
      </form>
    </div>
  );
}

function ThankYouPage({ order, onStore, onOrders }) {
  const fulfillment = order ? orderFulfillmentType(order) : 'physical';
  const isEbookOnly = fulfillment === 'ebook';
  const isMixed = fulfillment === 'mixed';
  const title = isEbookOnly ? 'Your e-book is ready' : 'Thank you for your order';
  const copy = isEbookOnly
    ? 'Your payment is successful. Login with the same mobile number and open My E-Books to download your personalized PDF.'
    : isMixed
      ? 'Your payment is successful. Your e-book access is ready, and we will send courier updates for the physical books.'
      : 'Your books order has been received. We will send order and delivery updates to your registered mobile number.';
  return (
    <main className="thankYouPage">
      <section className="thankYouCard">
        <div className="thankYouIcon">
          {isEbookOnly ? <FileText size={34} /> : <Check size={34} />}
        </div>
        <p className="thankYouEyebrow">Payment successful</p>
        <h1>{title}</h1>
        <p className="thankYouCopy">{copy}</p>
        {order && (
          <div className="thankYouSummary">
            <span>Order ID</span>
            <strong>{order.orderId || order.id}</strong>
          </div>
        )}
        <div className="thankYouActions">
          <button type="button" className="primaryButton" onClick={onOrders}>
            {isEbookOnly ? 'Open My E-Books' : 'View Orders'}
          </button>
          <button type="button" className="secondaryButton" onClick={onStore}>
            Continue Shopping
          </button>
        </div>
      </section>
    </main>
  );
}

function TrackingPage() {
  const initialNumber = new URLSearchParams(window.location.search).get('number') || '';
  const [trackingNumber, setTrackingNumber] = useState(initialNumber);
  const [message, setMessage] = useState('');
  const cleanNumber = String(trackingNumber || '').trim();

  function copyTrackingNumber() {
    if (!cleanNumber) return;
    if (!navigator.clipboard?.writeText) {
      setMessage('Copy this tracking number and paste it on the Professional Couriers tracking page.');
      return;
    }
    navigator.clipboard.writeText(cleanNumber).then(
      () => setMessage('Tracking number copied. Paste it on the Professional Couriers tracking page.'),
      () => setMessage('Copy this tracking number and paste it on the Professional Couriers tracking page.')
    );
  }

  function openOfficialTracking(event) {
    event?.preventDefault();
    if (!cleanNumber) {
      setMessage('Enter your Professional Courier tracking number.');
      return;
    }
    window.history.replaceState({}, '', `/track?number=${encodeURIComponent(cleanNumber)}`);
    window.open(PROFESSIONAL_COURIER_OFFICIAL_TRACKING_URL, '_blank', 'noopener,noreferrer');
    copyTrackingNumber();
  }

  useEffect(() => {
    if (initialNumber) {
      setMessage('Tracking number loaded. Open the official Professional Couriers page and paste it there.');
    }
  }, []);

  return (
    <main className="trackingPage">
      <section className="trackingHero">
        <div>
          <span className="trackingEyebrow">Professional Courier Tracking</span>
          <h1>Track your Muppadai books parcel</h1>
          <p>Use your tracking number on the official Professional Couriers tracking page. No paid tracking API is used.</p>
        </div>
        <Truck size={52} />
      </section>

      <section className="trackingPanel">
        <form className="trackingForm" onSubmit={openOfficialTracking}>
          <label>
            Tracking number
            <input value={trackingNumber} onChange={(e) => setTrackingNumber(e.target.value.trim())} placeholder="Enter tracking number" />
          </label>
          <button className="primaryButton" disabled={!cleanNumber}>Open Official Tracking</button>
        </form>
        {message && <div className="banner compact">{message}</div>}
      </section>

      {cleanNumber && (
        <section className="trackingResult">
          <div className="trackingSummary">
            <div>
              <span>Status</span>
              <strong>Check on official site</strong>
            </div>
            <div>
              <span>Courier</span>
              <strong>Professional Couriers</strong>
            </div>
            <div>
              <span>Tracking No.</span>
              <strong>{cleanNumber}</strong>
            </div>
            <div>
              <span>Cost</span>
              <strong>Free official tracking</strong>
            </div>
          </div>
          <div className="officialTrackingNote">
            <h2>How to check</h2>
            <ol>
              <li>Copy the tracking number.</li>
              <li>Open the Professional Couriers official website.</li>
              <li>Paste the number in the consignment tracking box and complete the captcha.</li>
            </ol>
            <div className="officialTrackingActions">
              <button type="button" className="secondaryButton" onClick={copyTrackingNumber}>Copy Tracking No.</button>
              <a className="primaryButton" href={PROFESSIONAL_COURIER_OFFICIAL_TRACKING_URL} target="_blank" rel="noreferrer">Open Professional Couriers</a>
            </div>
          </div>
        </section>
      )}
    </main>
  );
}

function buildAddress(form) {
  const street = String(form.street || '').trim();
  const postOffice = String(form.postOffice || '').trim();
  const district = String(form.district || '').trim();
  const state = String(form.state || '').trim();
  const pincode = String(form.pincode || '').trim();
  if (!street && !postOffice && !district && !state && !pincode) return '';
  return [street, postOffice, district, state].filter(Boolean).join(', ') + (pincode ? ' - ' + pincode : '');
}

function ebookDownloadFileName(item, book) {
  const base = item.ebookFileName || book?.ebook?.fileName || item.vn || item.variantName || item.bt || item.bookTitle || book?.title || 'muppadai-ebook';
  const clean = String(base)
    .replace(/\.[a-z0-9]+$/i, '')
    .replace(/[^\w\s.-]+/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .slice(0, 90) || 'muppadai-ebook';
  return clean.toLowerCase().endsWith('.pdf') ? clean : `${clean}.pdf`;
}

function contentDispositionFileName(value, fallback) {
  const match = String(value || '').match(/filename\*?=(?:UTF-8''|")?([^";]+)/i);
  if (!match) return fallback;
  try {
    return decodeURIComponent(match[1].replace(/"/g, '').trim()) || fallback;
  } catch {
    return fallback;
  }
}

function formatBytes(value) {
  const bytes = Number(value || 0);
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function hashString(value) {
  let hash = 2166136261;
  const text = String(value || '');
  for (let index = 0; index < text.length; index += 1) {
    hash ^= text.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }
  return hash >>> 0;
}

function seededRandom(seed) {
  let state = seed >>> 0;
  return () => {
    state += 0x6D2B79F5;
    let value = state;
    value = Math.imul(value ^ (value >>> 15), value | 1);
    value ^= value + Math.imul(value ^ (value >>> 7), value | 61);
    return ((value ^ (value >>> 14)) >>> 0) / 4294967296;
  };
}

function watermarkPageIndexes(pageCount, seedText) {
  if (pageCount <= 20) return Array.from({ length: pageCount }, (_, index) => index);
  const pages = new Set();
  const add = (index) => {
    if (index >= 0 && index < pageCount) pages.add(index);
  };
  [0, 1, 2, pageCount - 1].forEach(add);
  for (let index = 9; index < pageCount; index += 10) add(index);
  const random = seededRandom(hashString(seedText));
  const targetRandomCount = Math.min(14, Math.max(6, Math.ceil(pageCount * 0.08)));
  let guard = 0;
  while (pages.size < Math.min(pageCount, targetRandomCount + 4) && guard < pageCount * 4) {
    add(Math.floor(random() * pageCount));
    guard += 1;
  }
  return [...pages].sort((a, b) => a - b);
}

async function createClientWatermarkedPdf(arrayBuffer, { order, item, phone, filename }) {
  const pdfLib = await import('pdf-lib');
  const pdf = await pdfLib.PDFDocument.load(arrayBuffer, {
    ignoreEncryption: true,
    parseSpeed: pdfLib.ParseSpeeds?.Fastest
  });
  const font = await pdf.embedFont(pdfLib.StandardFonts.HelveticaBold);
  const pages = pdf.getPages();
  const studentName = String(order.delivery?.name || order.studentName || orderStudent(order) || 'Student').trim();
  const variant = item.vn || item.variantName || '';
  const watermark = `${studentName} | +91 ${phone}`;
  const footer = `${studentName} | +91 ${phone}`;
  const selectedIndexes = watermarkPageIndexes(pages.length, `${order.id || order.orderId || ''}|${phone}|${item.bid}|${variant}`);

  selectedIndexes.forEach((pageIndex) => {
    const page = pages[pageIndex];
    const { width, height } = page.getSize();
    const diagonalSize = Math.max(22, Math.min(42, width / 13));
    const diagonalWidth = font.widthOfTextAtSize(watermark, diagonalSize);
    page.drawText(watermark, {
      x: width / 2 - diagonalWidth / 2,
      y: height / 2,
      size: diagonalSize,
      font,
      color: pdfLib.rgb(0.07, 0.09, 0.34),
      opacity: 0.16,
      rotate: pdfLib.degrees(-32)
    });
    page.drawText(watermark, {
      x: 28,
      y: height - 30,
      size: 8,
      font,
      color: pdfLib.rgb(0.07, 0.09, 0.34),
      opacity: 0.72
    });
    page.drawText(footer, {
      x: 24,
      y: 20,
      size: 9,
      font,
      color: pdfLib.rgb(0.07, 0.09, 0.34),
      opacity: 0.86
    });
  });

  return {
    bytes: await pdf.save(),
    filename
  };
}

async function downloadSecureEbook(order, item, book, itemIndex, onProgress) {
  if (auth.authStateReady) await auth.authStateReady();
  const user = auth.currentUser;
  if (!user) throw new Error('Please login again with the purchase mobile number to download this e-book.');
  const token = await user.getIdToken();
  const params = new URLSearchParams({
    orderId: order.id || '',
    bookId: item.bid || '',
    item: String(itemIndex)
  });
  const response = await fetch(`/api/ebook-download?${params.toString()}`, {
    headers: { Authorization: `Bearer ${token}` }
  });
  if (!response.ok) {
    const text = await response.text().catch(() => '');
    let data = {};
    try {
      data = text ? JSON.parse(text) : {};
    } catch {
      data = {};
    }
    throw new Error(data.error || (text ? `Download service error ${response.status}. Please try again.` : 'Unable to prepare e-book download.'));
  }
  const fallback = ebookDownloadFileName(item, book);
  const filename = contentDispositionFileName(response.headers.get('Content-Disposition'), fallback);
  const total = Number(response.headers.get('Content-Length') || 0);
  const mode = response.headers.get('X-Muppadai-Download-Mode') || '';
  let sourceBlob;
  if (response.body?.getReader) {
    const reader = response.body.getReader();
    const chunks = [];
    let loaded = 0;
    onProgress?.({ loaded, total, percent: total ? 0 : null, filename, mode });
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      chunks.push(value);
      loaded += value.length;
      onProgress?.({
        loaded,
        total,
        percent: total ? Math.min(99, Math.round((loaded / total) * 100)) : null,
        filename,
        mode
      });
    }
    sourceBlob = new Blob(chunks, { type: 'application/pdf' });
    onProgress?.({ loaded, total: total || loaded, percent: 100, filename, mode });
  } else {
    sourceBlob = await response.blob();
    onProgress?.({ loaded: sourceBlob.size, total: sourceBlob.size, percent: 100, filename, mode });
  }
  onProgress?.({ loaded: sourceBlob.size, total: sourceBlob.size, percent: 100, filename, mode: 'client-watermarking' });
  const phone = orderPhone(order) || cleanPhone(user.phoneNumber || user.localId);
  const watermarked = await createClientWatermarkedPdf(await sourceBlob.arrayBuffer(), { order, item, phone, filename });
  const blob = new Blob([watermarked.bytes], { type: 'application/octet-stream' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = watermarked.filename;
  link.rel = 'noopener';
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 30000);
  return { filename: watermarked.filename, size: blob.size, mode: 'client-watermarked' };
}

function CheckoutField({ icon, tone, label, className = '', children }) {
  return (
    <div className={`fieldWithIcon ${className}`}>
      <span className={`fieldIcon ${tone}`}>{icon}</span>
      <label>
        {label}
        {children}
      </label>
    </div>
  );
}

function CustomerOrders({ books = [], onRated }) {
  const [phone, setPhone] = useState(() => getOtpSessionPhone() || getCustomerPhone());
  const [orders, setOrders] = useState([]);
  const [student, setStudent] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  async function load(nextPhone = phone) {
    const clean = cleanPhone(getOtpSessionPhone() || nextPhone);
    if (!clean) {
      setError('Login with your registered mobile number to view orders.');
      return;
    }
    setCustomerPhone(clean);
    setPhone(clean);
    setLoading(true);
    setError('');
    try {
      const [studentData, orderData] = await Promise.all([loadStudent(clean), loadCustomerOrders(clean)]);
      setStudent(studentData);
      setOrders(orderData);
    } catch (err) {
      setError(err.message || 'Unable to load orders.');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (phone) load();
  }, []);

  if (!getOtpSessionPhone()) {
    return (
      <section className="surface">
        <AuthBox onVerified={(verifiedPhone) => load(verifiedPhone)} />
      </section>
    );
  }

  const ebookOrders = orders.filter((order) => orderHasEbooks(order, books));
  const bookOrders = orders.filter((order) => orderFulfillmentType(order, books) !== 'ebook');

  return (
    <section className="surface">
      <div className="sectionToolbar">
        <div className="searchBox">
          <User size={17} />
          <input value={phone} readOnly placeholder="Registered phone number" />
        </div>
        <button className="primaryButton" onClick={load}>
          <RefreshCcw size={16} />
          Load Orders
        </button>
      </div>
      {error && <div className="banner danger">{error}</div>}
      {student && (
        <div className="customerStrip">
          <strong>{student.name || 'Customer'}</strong>
          <span>{student.phone || student.id}</span>
          <span>{student.district || student.address || ''}</span>
        </div>
      )}
      {loading ? (
        <div className="loading">Loading orders...</div>
      ) : orders.length ? (
        <div className="studentOrderSections">
          {!!ebookOrders.length && (
            <section className="studentOrderGroup">
              <div className="orderGroupHead">
                <h3>My E-Books</h3>
                <span>{ebookOrders.length} digital order{ebookOrders.length !== 1 ? 's' : ''}</span>
              </div>
              <div className="orderList">
                {ebookOrders.map((order) => (
                  <OrderCard order={order} key={`ebook-${order.id}`} books={books} sectionType="ebook" onRefresh={load} onRated={onRated} />
                ))}
              </div>
            </section>
          )}
          {!!bookOrders.length && (
            <section className="studentOrderGroup">
              <div className="orderGroupHead">
                <h3>Book Orders</h3>
                <span>{bookOrders.length} courier order{bookOrders.length !== 1 ? 's' : ''}</span>
              </div>
              <div className="orderList">
                {bookOrders.map((order) => (
                  <OrderCard order={order} key={`book-${order.id}`} books={books} sectionType="physical" onRefresh={load} onRated={onRated} />
                ))}
              </div>
            </section>
          )}
        </div>
      ) : (
        <EmptyState title="No orders found" body="Your book and e-book purchases will appear here after checkout." />
      )}
    </section>
  );
}

function OrderCard({ order, onRefresh, books = [], onRated, sectionType = 'auto' }) {
  const status = orderStatus(order);
  const fulfillment = orderFulfillmentType(order, books);
  const displayFulfillment = sectionType === 'ebook' ? 'ebook' : fulfillment;
  const statusCopy = orderStudentStatusCopy(order, displayFulfillment);
  const trackingNumber = String(order.trackingNumber || '').trim();
  const trackingHref = trackingNumber ? `${SITE_ORIGIN}/track?number=${encodeURIComponent(trackingNumber)}` : '';
  const [downloadBusy, setDownloadBusy] = useState('');
  const [downloadMessage, setDownloadMessage] = useState('');
  const [downloadProgress, setDownloadProgress] = useState(null);
  const downloadableItems = (order.items || [])
    .map((item, index) => {
      const book = books.find((entry) => entry.id === item.bid);
      const productType = item.productType || bookProductType(book || {});
      return {
        item,
        book,
        productType,
        itemIndex: index,
        fileName: ebookDownloadFileName(item, book)
      };
    })
    .filter((entry) => entry.productType === 'ebook' || entry.productType === 'combo' || entry.book?.ebook?.enabled);
  const showDownloads = sectionType === 'ebook' || fulfillment === 'ebook';

  async function handleEbookDownload(entry) {
    const key = `${entry.item.bid || 'ebook'}-${entry.itemIndex}`;
    setDownloadBusy(key);
    setDownloadMessage('Preparing your e-book download...');
    setDownloadProgress({ key, percent: 0, loaded: 0, total: 0, filename: entry.fileName });
    try {
      const result = await downloadSecureEbook(order, entry.item, entry.book, entry.itemIndex, (progress) => {
        setDownloadProgress({ key, ...progress });
        const amount = progress.total
          ? `${formatBytes(progress.loaded)} / ${formatBytes(progress.total)}`
          : formatBytes(progress.loaded);
        if (progress.mode === 'client-watermarking') {
          setDownloadMessage('Adding your mobile number and order watermark to the PDF...');
        } else {
          setDownloadMessage(progress.percent === 100 ? 'Saving PDF to your downloads...' : `Downloading PDF${amount ? ` (${amount})` : ''}`);
        }
      });
      setDownloadMessage(`Download started: ${result.filename}. Check your browser Downloads folder.`);
    } catch (err) {
      setDownloadMessage(err.message || 'Unable to download e-book.');
      setDownloadProgress(null);
    } finally {
      setDownloadBusy('');
      setTimeout(() => setDownloadProgress(null), 4000);
    }
  }

  async function markDelivered() {
    const ok = window.confirm('Please confirm only if you have received your parcel.');
    if (!ok) return;
    await updateOrderStatus(order.id, 'delivered');
    onRefresh?.();
  }
  return (
    <article className={displayFulfillment === 'ebook' ? 'orderCard ebookOrderCard' : 'orderCard'}>
      <div className="orderMain">
        <div className="orderTitle">{order.orderId || order.id}</div>
        <div className="muted">{dateLabel(order.createdAt)} • {totalUnits(order)} item(s)</div>
        <div className="lineItems">{(order.items || []).map((item) => `${item.bt || item.bookTitle} (${item.vn || item.variantName}) x${item.qty}`).join(' • ')}</div>
      </div>
      <div className="orderRight">
        <strong>{money(order.total)}</strong>
        <span className={displayFulfillment === 'ebook' ? 'status ebook' : 'status ' + status}>
          {displayFulfillment === 'ebook' ? 'E-Book Ready' : STATUS_LABELS[status] || status}
        </span>
      </div>
      <div className={`studentStatusBox ${statusCopy.tone}`}>
        <div className="studentStatusHead">
          <span>{statusCopy.title}</span>
          {statusCopy.date && <time>{statusCopy.date}</time>}
        </div>
        <p>{statusCopy.body}</p>
        {displayFulfillment !== 'ebook' && trackingNumber && (
          <div className="trackingActionRow">
            <span>Tracking No. <strong>{trackingNumber}</strong></span>
            <a className="trackOrderButton" href={trackingHref}>
              <Truck size={14} />
              Track order
            </a>
          </div>
        )}
        {displayFulfillment !== 'ebook' && status === 'shipped' && (
          <div className="deliveryConfirmBox">
            <button className="receivedButton" type="button" onClick={markDelivered}>
              Yes, I received my books
            </button>
            <small>Tap this only after receiving your parcel.</small>
          </div>
        )}
      </div>
      {showDownloads && !!downloadableItems.length && (
        <div className="ebookDownloadBox">
          <div>
            <strong>Secure E-Book Downloads</strong>
            <span>Each PDF is generated with your order details as a personal watermark.</span>
          </div>
          <div className="ebookDownloadList">
            {downloadableItems.map((entry) => {
              const key = `${entry.item.bid || 'ebook'}-${entry.itemIndex}`;
              return (
              <button type="button" onClick={() => handleEbookDownload(entry)} disabled={downloadBusy === key} key={`${entry.item.bid}-${entry.item.vn || entry.item.variantName}-${entry.itemIndex}`}>
                <FileText size={15} />
                <span>{downloadBusy === key ? 'Preparing PDF...' : entry.fileName}</span>
              </button>
              );
            })}
          </div>
          {downloadProgress && (
            <div className="ebookDownloadProgress" role="status" aria-live="polite">
              <div>
                <span>{downloadProgress.mode === 'client-watermarking' ? 'Adding watermark' : downloadProgress.percent === 100 ? 'Saving file' : 'Downloading file'}</span>
                <strong>{downloadProgress.percent === null ? formatBytes(downloadProgress.loaded) : `${downloadProgress.percent || 0}%`}</strong>
              </div>
              <div className="downloadProgressTrack">
                <i style={{ width: `${downloadProgress.percent === null ? 18 : downloadProgress.percent || 4}%` }} />
              </div>
              <small>{downloadProgress.filename || 'Muppadai e-book PDF'}</small>
            </div>
          )}
          {downloadMessage && <small className="ebookDownloadMessage">{downloadMessage}</small>}
        </div>
      )}
      {(displayFulfillment === 'ebook' || status === 'delivered') && (
        <div className="orderRatings">
          {(order.items || []).filter((item) => item.bid && (displayFulfillment === 'ebook' ? itemHasEbook(item, books) : true)).map((item, index) => {
            const book = books.find((entry) => entry.id === item.bid);
            return (
              <RatingBox
                key={`${item.bid}-${index}`}
                item={item}
                book={book}
                order={order}
                onRated={onRated}
              />
            );
          })}
        </div>
      )}
    </article>
  );
}

function RatingBox({ item, book, order, onRated }) {
  const [rating, setRating] = useState(5);
  const [review, setReview] = useState('');
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);
  const phone = orderPhone(order);

  async function submit() {
    setBusy(true);
    setMessage('');
    try {
      await saveBookRating({
        bookId: item.bid,
        bookTitle: book?.title || item.bt || item.bookTitle || '',
        phone,
        orderId: order.id,
        rating,
        review
      });
      setMessage('Thanks. Your verified rating is saved.');
      setReview('');
      onRated?.();
    } catch (err) {
      setMessage(err.message || 'Could not save rating.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="ratingBox">
      <div>
        <strong>Rate {item.bt || item.bookTitle || book?.title || 'this book'}</strong>
        <span>Verified purchase rating helps other students choose better.</span>
      </div>
      <div className="starPick" role="group" aria-label="Book rating">
        {[1, 2, 3, 4, 5].map((star) => (
          <button type="button" className={star <= rating ? 'active' : ''} onClick={() => setRating(star)} key={star}>★</button>
        ))}
      </div>
      <textarea value={review} onChange={(event) => setReview(event.target.value)} placeholder="Optional review" />
      <button type="button" onClick={submit} disabled={busy}>{busy ? 'Saving...' : 'Submit Rating'}</button>
      {message && <span className="ratingMessage">{message}</span>}
    </div>
  );
}

function ProfilePage({ books = [], onRated }) {
  const [phone, setPhone] = useState(() => getOtpSessionPhone() || getCustomerPhone());
  const [student, setStudent] = useState(null);
  const [orders, setOrders] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);

  async function refresh(nextPhone = phone) {
    const clean = cleanPhone(nextPhone);
    if (!clean) return;
    setLoading(true);
    try {
      const [studentData, orderData, notices] = await Promise.all([loadStudent(clean), loadCustomerOrders(clean), loadNotifications(clean)]);
      setStudent(studentData);
      setOrders(orderData);
      setNotifications(notices);
      setPhone(clean);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (phone) refresh(phone);
  }, []);

  async function readAll() {
    await markNotificationsRead(phone);
    await refresh(phone);
  }

  function logout() {
    setOtpSessionPhone('');
    setCustomerPhone('');
    setPhone('');
    setStudent(null);
    setOrders([]);
    setNotifications([]);
  }

  if (!getOtpSessionPhone()) {
    return (
      <section className="profileShell">
        <AuthBox onVerified={(verifiedPhone) => refresh(verifiedPhone)} />
      </section>
    );
  }

  const ebookOrders = orders.filter((order) => orderHasEbooks(order, books));
  const bookOrders = orders.filter((order) => orderFulfillmentType(order, books) !== 'ebook');

  return (
    <section className="profileShell">
      <div className="profileHeader">
        <div>
          <span>Logged in</span>
          <h2>{student?.name || 'Student'}</h2>
          <p>+91 {phone}</p>
        </div>
        <button className="secondaryButton" type="button" onClick={logout}>Logout</button>
      </div>
      {loading ? <div className="loading">Loading profile...</div> : (
        <>
          <div className="profileGrid">
            <div className="profileCard">
              <h3>Saved Address</h3>
              <p>{student?.address || buildAddress(student || {}) || 'No saved address yet.'}</p>
            </div>
            <div className="profileCard">
              <h3>Order Summary</h3>
              <p>{bookOrders.length} book order(s) • {ebookOrders.length} e-book order(s)</p>
            </div>
          </div>
          <div className="profileCard">
            <div className="profileCardHead">
              <h3>Notifications</h3>
              {!!notifications.length && <button type="button" onClick={readAll}>Mark all read</button>}
            </div>
            {notifications.length ? notifications.slice(0, 5).map((item) => (
              <div className={item.read === false ? 'noticeItem unread' : 'noticeItem'} key={item.id}>
                <strong>{item.title || 'Notification'}</strong>
                <span>{item.body || ''}</span>
              </div>
            )) : <p>No notifications yet.</p>}
          </div>
          <div className="profileCard">
            <div className="profileCardHead">
              <h3>My E-Books</h3>
              <span>{ebookOrders.length} item{ebookOrders.length !== 1 ? 's' : ''}</span>
            </div>
            {ebookOrders.length ? (
              <div className="orderList">
                {ebookOrders.slice(0, 5).map((order) => <OrderCard order={order} books={books} key={`profile-ebook-${order.id}`} sectionType="ebook" onRefresh={() => refresh(phone)} onRated={onRated} />)}
              </div>
            ) : <p>No e-book purchases yet.</p>}
          </div>
          <div className="profileCard">
            <div className="profileCardHead">
              <h3>Recent Book Orders</h3>
              <span>{bookOrders.length} order{bookOrders.length !== 1 ? 's' : ''}</span>
            </div>
            {bookOrders.length ? (
              <div className="orderList">
                {bookOrders.slice(0, 5).map((order) => <OrderCard order={order} books={books} key={`profile-book-${order.id}`} sectionType="physical" onRefresh={() => refresh(phone)} onRated={onRated} />)}
              </div>
            ) : <p>No physical book orders yet.</p>}
          </div>
        </>
      )}
    </section>
  );
}

function AuthBox({ onVerified }) {
  const [phone, setPhone] = useState(() => getCustomerPhone());
  const [otp, setOtp] = useState('');
  const [step, setStep] = useState('phone');
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');

  async function requestOtp() {
    if (!isValidIndianMobile(phone)) {
      setMessage('Enter a valid 10-digit mobile number.');
      return;
    }
    setBusy(true);
    setMessage('');
    try {
      await sendOtp(phone);
      setStep('otp');
      setMessage('OTP sent to +91 ' + cleanPhone(phone));
    } catch (err) {
      setMessage(err.message || 'Could not send OTP.');
    } finally {
      setBusy(false);
    }
  }

  async function verifyLogin() {
    setBusy(true);
    setMessage('');
    try {
      await verifyOtp(phone, otp);
      onVerified?.(cleanPhone(phone));
    } catch (err) {
      setMessage(err.message || 'OTP verification failed.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="authCard">
      <h2>Student Login</h2>
      <p>Login once to view profile, notifications and saved delivery details.</p>
      <div className="phoneInputWrap">
        <span>+91</span>
        <input inputMode="numeric" maxLength={10} value={phone} onChange={(event) => setPhone(cleanPhone(event.target.value))} placeholder="10-digit mobile number" />
      </div>
      {step === 'otp' && <input className="otpSingleInput" inputMode="numeric" maxLength={6} value={otp} onChange={(event) => setOtp(event.target.value.replace(/\D/g, '').slice(0, 6))} placeholder="6-digit OTP" />}
      {message && <div className="banner compact">{message}</div>}
      {step === 'phone' ? (
        <button className="primaryButton wide" type="button" onClick={requestOtp} disabled={busy}>{busy ? 'Sending...' : 'Send OTP'}</button>
      ) : (
        <button className="primaryButton wide" type="button" onClick={verifyLogin} disabled={busy || otp.length !== 6}>{busy ? 'Verifying...' : 'Login'}</button>
      )}
    </div>
  );
}

function EmptyState({ title, body }) {
  return (
    <div className="emptyState">
      <Package size={32} />
      <strong>{title}</strong>
      <span>{body}</span>
    </div>
  );
}

export default App;
